import { type DragEvent, FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  api,
  apiFile,
  ApiError,
  type CaseDocument,
  type CalculationResult,
  type AllocationPreparation,
  type DataImportBatch,
  getSession,
  type GibCompatibility,
  type IntegratorDefinition,
  type IntegratorStatus,
  type Invoice,
  type InvoiceImportBatch,
  type InvoiceImportIssue,
  type InvoiceSyncJob,
  type LegislationChange,
  type RefundCase,
  type RefundEntry,
  type RefundWorkspaceFamily,
  type RefundWorkJob,
  type SemanticReadiness,
  type Session,
  type SourceReconciliation,
  type SubmissionCapability,
  type SubmissionPackage,
  type TaxRuleSnapshot,
  type Taxpayer,
  type TaxpayerDetail,
  type User
} from "./api";
import { GibCompatibilityPanel } from "./GibCompatibilityPanel";
import { DebugTransferContext, DebugTransferControls } from "./components/DebugTransferControls";
import { FullscreenProgress } from "./components/FullscreenProgress";
import { MoneyInput } from "./components/MoneyInput";
import { InvoicePreviewModal } from "./components/InvoicePreviewModal";
import { SmartRefundWorkspace } from "./refunds/SmartRefundWorkspace";
import { ReportsPage } from "./reports/ReportsPage";
import { CustomsPage } from "./customs/CustomsPage";
import { calculationAmount, formatMoney, moneyNumber, formatMoneyText } from "./formatting";

type Section = "overview" | "taxpayers" | "invoices" | "refunds" | "customs" | "reports" | "legislation";
type InvoiceReviewRequest = {
  refundCaseId: number;
  invoiceIds: number[];
  code: string;
  title: string;
  instruction?: string;
};

const statusLabels: Record<string, string> = {
  draft: "Hazırlanıyor",
  review: "Kontrolde",
  approved: "Onaylandı",
  ready: "GİB'e hazır",
  completed: "Sonuçlandı",
  queued: "Kuyrukta",
  running: "Çalışıyor",
  succeeded: "Başarılı",
  partial: "Kısmi",
  issues: "İnceleme gerekli",
  failed: "Başarısız"
};

function localDate(value: Date) {
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
}

function Login({
  authProvider,
  initialError,
  onLogin
}: {
  authProvider: string;
  initialError?: string;
  onLogin: (user: User) => void;
}) {
  const [open, setOpen] = useState(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState(initialError || "");
  const [busy, setBusy] = useState(false);
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactMessage, setContactMessage] = useState("");
  const [contactBusy, setContactBusy] = useState(false);
  const [contactFeedback, setContactFeedback] = useState("");
  const [contactSent, setContactSent] = useState(false);
  const isMock = authProvider === "mock_microsoft";

  function startMicrosoftLogin() {
    setBusy(true);
    setError("");
    window.location.assign("/api/auth/microsoft");
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const result = await api<{ authenticated: true; user: User }>("/api/auth/mock-microsoft", {
        method: "POST",
        body: JSON.stringify({ email, full_name: fullName })
      });
      onLogin(result.user);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Giriş yapılamadı.");
    } finally {
      setBusy(false);
    }
  }

  async function submitContact(event: FormEvent) {
    event.preventDefault();
    setContactBusy(true);
    setContactFeedback("");
    setContactSent(false);
    try {
      await api<{ sent: true }>("/api/auth/contact", {
        method: "POST",
        body: JSON.stringify({
          full_name: contactName,
          email: contactEmail,
          message: contactMessage
        })
      });
      setContactName("");
      setContactEmail("");
      setContactMessage("");
      setContactSent(true);
      setContactFeedback("Mesajınız gönderildi.");
    } catch (reason) {
      setContactFeedback(reason instanceof Error ? reason.message : "Mesaj gönderilemedi.");
    } finally {
      setContactBusy(false);
    }
  }

  return (
    <main className="login-shell">
      <section className="login-panel">
        <div className="brand-lockup"><span className="brand-mark">V</span><span>VRP</span></div>
        <div className="login-copy">
          <p className="eyebrow">VAT RECOVERY PLATFORM</p>
          <h1>KDV iade sürecinin<br />tek çalışma alanı.</h1>
        </div>
        <button
          className="microsoft-button"
          disabled={busy}
          onClick={() => isMock ? setOpen(true) : startMicrosoftLogin()}
        >
          <span className="microsoft-grid"><i /><i /><i /><i /></span>
          {busy ? "Microsoft'a yönlendiriliyor…" : "Microsoft ile giriş yap"}
        </button>
        {!isMock && error && <div className="form-error login-auth-error">{error}</div>}
        {isMock && <p className="temporary-note">Geçici kimlik doğrulama · yalnızca yerel geliştirme</p>}
      </section>
      <aside className="login-aside">
        <div className="login-steps" aria-label="VRP işlem adımları">
          <div><strong>Faturaları çek</strong></div>
          <div><strong>Beyannameleri yükle</strong></div>
          <div><strong>Raporunu yükle</strong></div>
          <div><strong>Defteri yükle &amp; Hesapla</strong></div>
        </div>
        <form className="contact-form" onSubmit={submitContact}>
          <div className="contact-heading">
            <p className="eyebrow">İLETİŞİME GEÇ</p>
            <strong>Bize mesaj bırakın</strong>
          </div>
          <div className="contact-fields">
            <label>İsim soyisim<input required type="text" autoComplete="name" maxLength={120} value={contactName} onChange={(e) => setContactName(e.target.value)} /></label>
            <label>E-posta<input required type="email" autoComplete="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} /></label>
          </div>
          <label>Mesaj<textarea required maxLength={4000} rows={3} value={contactMessage} onChange={(e) => setContactMessage(e.target.value)} /></label>
          {contactFeedback && <div className={contactSent ? "contact-success" : "contact-error"} role="status">{contactFeedback}</div>}
          <button className="contact-submit" disabled={contactBusy}>{contactBusy ? "Gönderiliyor…" : "Gönder"}</button>
        </form>
      </aside>
      {isMock && open && (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setOpen(false)}>
          <div className="modal" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setOpen(false)} aria-label="Kapat">×</button>
            <p className="eyebrow">GEÇİCİ MICROSOFT GİRİŞİ</p>
            <h2>Bilgilerinizi girin</h2>
            <p>Microsoft Entra bağlantısı tamamlanana kadar ad soyad ve kurumsal e-posta bilgilerinizle devam edebilirsiniz.</p>
            <form onSubmit={submit}>
              <label>Ad soyad<input autoFocus required type="text" autoComplete="name" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Ad Soyad" /></label>
              <label>Kurumsal e-posta adresi<input required type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ad.soyad@sirket.com" /></label>
              {error && <div className="form-error">{error}</div>}
              <button className="primary-button" disabled={busy}>{busy ? "Giriş yapılıyor…" : "Devam et"}</button>
            </form>
          </div>
        </div>
      )}
    </main>
  );
}

function Stat({ label, value, meta }: { label: string; value: number | string; meta: string }) {
  return <article className="stat-card"><span>{label}</span><strong>{value}</strong><small>{meta}</small></article>;
}

function Empty({ children }: { children: string }) {
  return <div className="empty-state"><span>—</span><p>{children}</p></div>;
}

function LegislationUpdates({ changes, canTaxApprove, canTechnicalApprove, onUpdated }: {
  changes: LegislationChange[];
  canTaxApprove: boolean;
  canTechnicalApprove: boolean;
  onUpdated: () => void | Promise<void>;
}) {
  const [selectedId, setSelectedId] = useState<number | null>(changes[0]?.id || null);
  const selected = changes.find((item) => item.id === selectedId) || changes[0] || null;
  const [notes, setNotes] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [codes, setCodes] = useState("");
  const [families, setFamilies] = useState("");
  const [blockFinal, setBlockFinal] = useState(false);
  const [version, setVersion] = useState("");
  const [rulesJson, setRulesJson] = useState("[]");
  const [testEvidence, setTestEvidence] = useState("");
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState("");

  useEffect(() => {
    if (!selected) return;
    setEffectiveDate(selected.effective_date || "");
    setCodes(selected.affected_transaction_codes.join(", "));
    setFamilies(selected.affected_families.join(", "));
    setBlockFinal(selected.requires_final_block);
    setVersion(`mevzuat-${selected.id}-${selected.effective_date || "tarih"}`);
    setRulesJson("[]");
    setNotes("");
    setFeedback("");
  }, [selected?.id]);

  async function taxDecision(decision: "approved" | "rejected") {
    if (!selected) return;
    setBusy(true); setFeedback("");
    try {
      await api(`/api/gib/legislation/changes/${selected.id}/tax-review`, {
        method: "POST",
        body: JSON.stringify({
          decision,
          notes,
          draft: {
            effective_date: effectiveDate || null,
            affected_transaction_codes: codes.split(",").map((item) => item.trim()).filter(Boolean),
            affected_families: families.split(",").map((item) => item.trim()).filter(Boolean),
            requires_final_block: blockFinal
          }
        })
      });
      setFeedback("Vergi incelemesi kaydedildi.");
      await onUpdated();
    } catch (reason) { setFeedback(reason instanceof Error ? reason.message : "Onay kaydedilemedi."); }
    finally { setBusy(false); }
  }

  async function technicalDecision(decision: "approved" | "rejected") {
    if (!selected) return;
    setBusy(true); setFeedback("");
    try {
      const parsedRules = decision === "approved" ? JSON.parse(rulesJson) : [];
      const ruleDefinitions = Array.isArray(parsedRules)
        ? parsedRules.filter((item) => item && typeof item === "object" && Object.keys(item).length)
        : [];
      await api(`/api/gib/legislation/changes/${selected.id}/technical-review`, {
        method: "POST",
        body: JSON.stringify({
          decision,
          notes,
          version,
          rule_definitions: ruleDefinitions,
          test_results: decision === "approved" ? { status: "passed", evidence: testEvidence } : {}
        })
      });
      setFeedback(decision === "approved" ? "Onaylı kural paketi yayınlandı." : "Teknik inceleme reddedildi.");
      await onUpdated();
    } catch (reason) { setFeedback(reason instanceof Error ? reason.message : "Teknik karar kaydedilemedi."); }
    finally { setBusy(false); }
  }

  async function triage() {
    if (!selected) return;
    setBusy(true); setFeedback("");
    try {
      await api(`/api/gib/legislation/changes/${selected.id}/triage`, { method: "POST" });
      setFeedback("Değişiklik vergi incelemesine alındı.");
      await onUpdated();
    } catch (reason) { setFeedback(reason instanceof Error ? reason.message : "İşlem başlatılamadı."); }
    finally { setBusy(false); }
  }

  const columns = [
    ["detected", "Yeni değişiklikler"],
    ["tax_review", "Vergi onayı bekleyen"],
    ["technical_review", "Teknik onay bekleyen"],
    ["published", "Yayınlanan kurallar"],
    ["rejected", "Reddedilen"]
  ] as const;

  return <section className="legislation-page">
    <div className="legislation-board">
      {columns.map(([status, label]) => <article className="panel legislation-column" key={status}>
        <div className="panel-head"><div><p className="eyebrow">{status.replace("_", " ")}</p><h2>{label}</h2></div><span className="count">{changes.filter((item) => item.status === status).length}</span></div>
        {changes.filter((item) => item.status === status).map((item) => <button className={`legislation-card ${selected?.id === item.id ? "selected" : ""}`} key={item.id} onClick={() => setSelectedId(item.id)}>
          <strong>{item.source.title}</strong>
          <small>{item.effective_date || "Yürürlük tarihi incelenecek"} · {item.affected_transaction_codes.join(", ") || "Kod etkisi incelenecek"}</small>
          <span>{item.summary.slice(0, 150)}</span>
        </button>)}
        {!changes.some((item) => item.status === status) && <Empty>Bu aşamada kayıt yok.</Empty>}
      </article>)}
    </div>
    {selected && <article className="panel legislation-detail">
      <div className="panel-head"><div><p className="eyebrow">KAYNAK VE ETKİ TASLAĞI</p><h2>{selected.source.title}</h2></div><a href={selected.source.url} target="_blank" rel="noreferrer">Resmî kaynağı aç</a></div>
      <div className="legislation-meta"><span><small>Belge tarihi</small><strong>{selected.snapshot.document_date || "—"}</strong></span><span><small>İndirme zamanı</small><strong>{new Date(selected.snapshot.fetched_at).toLocaleString("tr-TR")}</strong></span><span><small>SHA-256</small><code>{selected.snapshot.sha256}</code></span><span><small>Taslak üreticisi</small><strong>{selected.draft_provider}</strong></span></div>
      <p>{selected.summary}</p>
      <div className="legislation-impact"><span><small>Etkilenen kodlar</small><strong>{selected.affected_transaction_codes.join(", ") || "İncelenecek"}</strong></span><span><small>Etkilenen aileler</small><strong>{selected.affected_families.join(", ") || "İncelenecek"}</strong></span><span><small>Hesap kuralları</small><strong>{selected.affected_rule_codes.join(", ") || "İncelenecek"}</strong></span></div>
      <details open><summary>Kaynak farkı</summary><pre className="legislation-diff">{selected.snapshot.diff || "İlk kaynak sürümü; önceki sürüm farkı yok."}</pre></details>
      {!!selected.proposed_tests.length && <details><summary>Önerilen test senaryoları</summary><ul>{selected.proposed_tests.map((item) => <li key={item}>{item}</li>)}</ul></details>}
      {!!selected.approvals.length && <div className="legislation-approvals">{selected.approvals.map((item) => <span key={item.stage}><strong>{item.stage === "tax" ? "Vergi onayı" : "Teknik onay"}: {item.decision}</strong><small>{item.reviewer} · {item.notes || "Not yok"}</small></span>)}</div>}
      {selected.status === "detected" && canTaxApprove && <div className="review-actions"><button className="primary-button" disabled={busy} onClick={() => void triage()}>Vergi incelemesine al</button></div>}
      {selected.status === "tax_review" && canTaxApprove && <div className="legislation-review-form">
        <label>Yürürlük tarihi<input type="date" value={effectiveDate} onChange={(event) => setEffectiveDate(event.target.value)} /></label>
        <label>İşlem kodları<input value={codes} onChange={(event) => setCodes(event.target.value)} placeholder="301, 338" /></label>
        <label>İşlem aileleri<input value={families} onChange={(event) => setFamilies(event.target.value)} /></label>
        <label className="checkbox-label"><input type="checkbox" checked={blockFinal} onChange={(event) => setBlockFinal(event.target.checked)} />Yürürlükteyse yalnız etkilenen ailede nihai hesabı engelle</label>
        <label className="span-all">Vergi uzmanı notu<textarea value={notes} onChange={(event) => setNotes(event.target.value)} /></label>
        <div className="span-all review-actions"><button className="secondary-button" disabled={busy} onClick={() => void taxDecision("rejected")}>Reddet</button><button className="primary-button" disabled={busy || !effectiveDate} onClick={() => void taxDecision("approved")}>Vergi onayı ver</button></div>
      </div>}
      {selected.status === "technical_review" && canTechnicalApprove && <div className="legislation-review-form">
        <label>Paket sürümü<input value={version} onChange={(event) => setVersion(event.target.value)} /></label>
        <label className="span-all">Tarihli kural tanımları (JSON)<textarea className="code-input" value={rulesJson} onChange={(event) => setRulesJson(event.target.value)} /></label>
        <label className="span-all">Başarılı regresyon testi kanıtı<textarea value={testEvidence} onChange={(event) => setTestEvidence(event.target.value)} /></label>
        <label className="span-all">Teknik inceleme notu<textarea value={notes} onChange={(event) => setNotes(event.target.value)} /></label>
        <div className="span-all review-actions"><button className="secondary-button" disabled={busy} onClick={() => void technicalDecision("rejected")}>Reddet</button><button className="primary-button" disabled={busy || !version || !testEvidence} onClick={() => void technicalDecision("approved")}>Testlerle yayınla</button></div>
      </div>}
      {selected.rule_package && <div className="notice success"><strong>{selected.rule_package.version}</strong> · {selected.rule_package.valid_from} tarihinden itibaren yayınlandı; {selected.rule_package.published_rule_ids.length} kural sürümü oluşturuldu.</div>}
      {feedback && <div className="notice warning">{feedback}</div>}
    </article>}
  </section>;
}

function PendingApproval({ user, onUpdated, onRefresh, onSignOut }: { user: User; onUpdated: (user: User) => void; onRefresh: () => void; onSignOut: () => void }) {
  const [officeId, setOfficeId] = useState(String(user.access_request?.organization_id || user.eligible_offices[0]?.id || ""));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function requestAccess() {
    if (!officeId) return;
    setBusy(true);
    setError("");
    try {
      const result = await api<{ authenticated: true; user: User }>("/api/auth/access-request", {
        method: "POST",
        body: JSON.stringify({ organization_id: Number(officeId) })
      });
      onUpdated(result.user);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Erişim talebi gönderilemedi.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="approval-shell">
      <section className="approval-card">
        <div className="brand-lockup"><span className="brand-mark">V</span><span>VRP</span></div>
        <p className="eyebrow">HESAP ONAYI</p>
        <h1>{user.access_request ? "Erişim talebiniz bekliyor" : "Muhasebe büronuzu seçin"}</h1>
        <p>Kurumsal e-posta domaininizle eşleşen muhasebe bürosunu seçin. Büro Admini veya sistem yöneticisi size Muhasebeci ya da Direktör rolü verecektir.</p>
        {user.eligible_offices.length ? <label className="approval-office-select">Muhasebe bürosu<select value={officeId} disabled={Boolean(user.access_request)} onChange={(event) => setOfficeId(event.target.value)}>{user.eligible_offices.map((office) => <option value={office.id} key={office.id}>{office.name}</option>)}</select></label> : <div className="form-error">E-posta domaininizle eşleşen muhasebe bürosu bulunamadı.</div>}
        {user.access_request && <div className="approval-steps"><span className="pending">{user.access_request.organization_name} · Admin onayı bekleniyor</span></div>}
        {error && <div className="form-error">{error}</div>}
        {!user.access_request && <button className="primary-button approval-refresh" disabled={busy || !officeId} onClick={() => void requestAccess()}>{busy ? "Gönderiliyor…" : "Erişim Talep Et"}</button>}
        <button className="primary-button approval-refresh" onClick={onRefresh}>Durumu Güncelle</button>
        <button className="secondary-button" onClick={onSignOut}>Oturumu kapat</button>
      </section>
    </main>
  );
}

function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [section, setSection] = useState<Section>("overview");
  const [taxpayers, setTaxpayers] = useState<Taxpayer[]>([]);
  const [cases, setCases] = useState<RefundCase[]>([]);
  const [integratorStatus, setIntegratorStatus] = useState<IntegratorStatus | null>(null);
  const [jobs, setJobs] = useState<InvoiceSyncJob[]>([]);
  const [providers, setProviders] = useState<IntegratorDefinition[]>([]);
  const [compatibility, setCompatibility] = useState<GibCompatibility | null>(null);
  const [legislationChanges, setLegislationChanges] = useState<LegislationChange[]>([]);
  const [activeInvoice, setActiveInvoice] = useState<Invoice | null>(null);
  const [invoiceReviewRequest, setInvoiceReviewRequest] = useState<InvoiceReviewRequest | null>(null);
  const [error, setError] = useState("");
  const [taxpayerForm, setTaxpayerForm] = useState(false);
  const [editingTaxpayerId, setEditingTaxpayerId] = useState<number | null>(null);
  const [caseForm, setCaseForm] = useState(false);
  const [debugImportVersion, setDebugImportVersion] = useState(0);
  const [debugImportMessage, setDebugImportMessage] = useState("");
  const [activeCase, setActiveCase] = useState<RefundCase | null>(null);
  const [deletingCase, setDeletingCase] = useState<RefundCase | null>(null);
  const [deletingTaxpayer, setDeletingTaxpayer] = useState<Taxpayer | null>(null);
  const deletingCaseIdRef = useRef<number | null>(null);
  const refundErrorBannerRef = useRef<HTMLDivElement | null>(null);
  const [taxRules, setTaxRules] = useState<TaxRuleSnapshot | null>(null);
  const [caseTaxpayerFilter, setCaseTaxpayerFilter] = useState("all");
  const [caseStatusFilter, setCaseStatusFilter] = useState("open");

  useEffect(() => { getSession().then(setSession).catch((e) => setError(e.message)); }, []);

  const loadJobs = useCallback(async () => {
    const jobData = await api<InvoiceSyncJob[]>("/api/integrators/sync-jobs");
    setJobs(jobData);
    return jobData;
  }, []);

  const load = useCallback(async function load() {
    setError("");
    try {
      const [taxpayerData, caseData, statusData, jobData, providerData, ruleData, legislationData] = await Promise.all([
        api<Taxpayer[]>("/api/taxpayers"),
        api<RefundCase[]>("/api/refund-cases"),
        api<IntegratorStatus>("/api/integrators/status"),
        api<InvoiceSyncJob[]>("/api/integrators/sync-jobs"),
        api<IntegratorDefinition[]>("/api/integrators/providers"),
        api<TaxRuleSnapshot>("/api/tax-rules"),
        api<LegislationChange[]>("/api/gib/legislation/changes")
      ]);
      setTaxpayers(taxpayerData);
      setCases(caseData);
      setIntegratorStatus(statusData);
      setCompatibility(statusData.reference);
      setJobs(jobData);
      setProviders(providerData);
      setTaxRules(ruleData);
      setLegislationChanges(legislationData);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Veriler alınamadı.");
    }
  }, []);

  useEffect(() => { if (session?.authenticated && !session.user.approval_pending) void load(); }, [session, load]);

  useEffect(() => {
    if (!error || section !== "refunds") return;
    const frame = window.requestAnimationFrame(() => {
      refundErrorBannerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      refundErrorBannerRef.current?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [error, section]);

  const activeJobCount = useMemo(
    () => jobs.filter((job) => job.status === "queued" || job.status === "running").length,
    [jobs]
  );

  useEffect(() => {
    if (!session?.authenticated || session.user.approval_pending || activeJobCount === 0) return;
    let cancelled = false;
    let timer = 0;
    const poll = async () => {
      try {
        const updatedJobs = await loadJobs();
        if (cancelled) return;
        if (updatedJobs.some((job) => job.status === "queued" || job.status === "running")) {
          timer = window.setTimeout(() => void poll(), 3000);
        } else {
          await load();
        }
      } catch (reason) {
        if (cancelled) return;
        setError(reason instanceof Error ? reason.message : "İş durumu alınamadı.");
        timer = window.setTimeout(() => void poll(), 5000);
      }
    };
    timer = window.setTimeout(() => void poll(), 3000);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [session, activeJobCount, load, loadJobs]);

  const openCases = useMemo(() => cases.filter((item) => !item.deleted_at && item.status !== "completed").length, [cases]);
  const visibleCases = useMemo(() => cases.filter((item) => (
    (caseTaxpayerFilter === "all" || String(item.taxpayer) === caseTaxpayerFilter)
    && (caseStatusFilter === "all" || (caseStatusFilter === "open" ? item.status !== "completed" : item.status === caseStatusFilter))
  )), [caseStatusFilter, caseTaxpayerFilter, cases]);

  if (session === null) return <div className="loading">VRP hazırlanıyor…</div>;
  if (!session.authenticated) return <Login authProvider={session.auth_provider} initialError={session.auth_error} onLogin={(user) => setSession({ authenticated: true, user })} />;

  const user = session.user;

  async function signOut() {
    await api("/api/auth/logout", { method: "POST" });
    setSession({ authenticated: false, auth_provider: user.auth_provider });
  }

  async function retryJob(id: number) {
    setError("");
    try {
      await api(`/api/integrators/sync-jobs/${id}/retry`, { method: "POST" });
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "İş yeniden kuyruğa alınamadı.");
    }
  }

  async function deleteJob(job: InvoiceSyncJob) {
    const confirmed = window.confirm(
      `${job.taxpayer_vkn} için ${job.start_date} — ${job.end_date} işlemi ve bu işlemde çekilen tüm faturalar kalıcı olarak silinsin mi?`
    );
    if (!confirmed) return;
    setError("");
    try {
      await api(`/api/integrators/sync-jobs/${job.id}`, { method: "DELETE" });
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "İşlem silinemedi.");
    }
  }

  async function deleteRefundCase(refundCase: RefundCase) {
    if (deletingCaseIdRef.current !== null) return;
    const confirmed = window.confirm(
      `“${refundCase.title}” KDV iade dosyası kalıcı olarak silinsin mi?\n\n`
      + "Faturalar, fatura belgeleri ve şirket için kaydedilmiş yıl sonu stokları korunacak. Bu iade dosyasına ait diğer yüklenen veriler ve hesaplama sonuçları kalıcı olarak silinecek."
    );
    if (!confirmed) return;
    deletingCaseIdRef.current = refundCase.id;
    setDeletingCase(refundCase);
    setError("");
    try {
      await api(`/api/refund-cases/${refundCase.id}`, { method: "DELETE" });
      setCases((current) => current.filter((item) => item.id !== refundCase.id));
      if (activeCase?.id === refundCase.id) setActiveCase(null);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "İade dosyası silinemedi.");
    } finally {
      deletingCaseIdRef.current = null;
      setDeletingCase(null);
    }
  }

  async function deleteTaxpayer(taxpayer: Taxpayer) {
    if (deletingTaxpayer !== null) return;
    const confirmed = window.confirm(
      `“${taxpayer.title}” (VKN ${taxpayer.vkn}) kalıcı olarak silinsin mi?\n\n`
      + "Bu mükellefe ait bütün KDV iade dosyaları, faturalar ve yüklenen belgeler kalıcı olarak silinecek. Bu işlem geri alınamaz."
    );
    if (!confirmed) return;
    setDeletingTaxpayer(taxpayer);
    setError("");
    try {
      await api(`/api/taxpayers/${taxpayer.id}`, { method: "DELETE" });
      setTaxpayers((current) => current.filter((item) => item.id !== taxpayer.id));
      setCases((current) => current.filter((item) => item.taxpayer !== taxpayer.id));
      if (editingTaxpayerId === taxpayer.id) setEditingTaxpayerId(null);
      if (activeCase?.taxpayer === taxpayer.id) setActiveCase(null);
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Mükellef silinemedi.");
    } finally {
      setDeletingTaxpayer(null);
    }
  }

  if (user.approval_pending) {
    return <PendingApproval user={user} onUpdated={(updatedUser) => setSession({ authenticated: true, user: updatedUser })} onRefresh={() => getSession().then(setSession)} onSignOut={signOut} />;
  }

  const org = user.memberships[0]?.organization__name || "VRP";
  const role = user.memberships[0]?.role;
  const canPrepare = role === "accountant" || Boolean(user.can_transfer_debug_data);
  const canApprove = role === "director";

  return (
    <DebugTransferContext.Provider value={{ canDownload: Boolean(user.can_transfer_debug_data), canUpload: Boolean(user.can_import_debug_data),
      onImported: async (caseId, message) => { setDebugImportMessage(message || ""); await load(); if (caseId) { setActiveCase(await api<RefundCase>(`/api/refund-cases/${caseId}`)); setDebugImportVersion(value => value + 1); } }
    }}><div className="app-shell">
      <FullscreenProgress
        visible={deletingCase !== null || deletingTaxpayer !== null}
        message={deletingTaxpayer
          ? `“${deletingTaxpayer.title}” ve tüm verileri kalıcı olarak siliniyor…`
          : deletingCase
            ? `“${deletingCase.title}” iade dosyası kalıcı olarak siliniyor…`
            : undefined}
      />
      <aside className="sidebar">
        <div className="sidebar-brand"><div className="brand-lockup"><span className="brand-mark">V</span><span>VRP</span></div><small>VAT Recovery Platform</small></div>
        <nav>
          {([[
            "overview", "Genel görünüm"
          ], ["taxpayers", "Mükellefler"], ["invoices", "Faturalar"], ["customs", "GGB/GÇB"], ["refunds", "İade dosyaları"], ["reports", "Raporlar"], ["legislation", "Mevzuat Güncellemeleri"]] as [Section, string][]).map(([key, label]) => (
            <button key={key} className={section === key ? "active" : ""} onClick={() => { setSection(key); if (key === "refunds") setActiveCase(null); if (key === "invoices") setInvoiceReviewRequest(null); }}><span />{label}</button>
          ))}
        </nav>
        <div className="sidebar-footer"><small>{org}</small><strong>{user.email}</strong><button onClick={signOut}>Oturumu kapat</button></div>
      </aside>
      <main className="content">
        <header className="topbar">
          <div><h1>{section === "overview" ? "Genel görünüm" : section === "taxpayers" ? "Mükellefler" : section === "invoices" ? "Faturalar" : section === "customs" ? "GGB/GÇB" : section === "reports" ? "Raporlar" : section === "legislation" ? "Mevzuat Güncellemeleri" : "KDV iade dosyaları"}</h1></div>
          <div className="environment"><i /> {canApprove ? "DİREKTÖR · İNCELEME" : "MUHASEBECİ · HAZIRLIK"}</div>
        </header>
        {error && <div className={`banner-error ${section === "refunds" ? "refund-error-banner" : ""}`} ref={section === "refunds" ? refundErrorBannerRef : undefined} role="alert" tabIndex={section === "refunds" ? -1 : undefined}>{error}<button aria-label="Hata mesajını kapat" onClick={() => setError("")}>×</button></div>}

        {debugImportMessage && <div className="notice" role="status">{debugImportMessage}<button className="text-button" aria-label="Aktarım mesajını kapat" onClick={() => setDebugImportMessage("")}>×</button></div>}
        {section === "overview" && <>
          <section className="stats-grid">
            <Stat label="Aktif mükellef" value={taxpayers.length} meta="organizasyon kapsamında" />
            <Stat label="Açık iade dosyası" value={openCases} meta="hazırlık ve kontrol aşamasında" />
            <Stat label="Fatura belgesi" value={integratorStatus?.invoices ?? 0} meta="normalize edilmiş kayıt" />
            <Stat label="GİB Excel uyumu" value={compatibility ? `${compatibility.exportable_outputs}/${compatibility.total_outputs} hazır` : "—"} meta={compatibility?.summary ?? "Kontrol ediliyor"} />
          </section>
          <section className="panel-grid">
            <article className="panel span-two"><div className="panel-head"><div><p className="eyebrow">SON İADE DOSYALARI</p><h2>Çalışma kuyruğu</h2></div><button className="text-button" onClick={() => setSection("refunds")}>Tümünü gör</button></div>
              {cases.length ? <div className="table-list">{cases.slice(0, 5).map((item) => <div className="table-row" key={item.id}><span><strong>{item.title}</strong><small>{item.taxpayer_vkn} · {item.taxpayer_title}</small></span><span>{item.period_end}</span><em className={`pill ${item.status}`}>{statusLabels[item.status] || item.status}</em></div>)}</div> : <Empty>Henüz iade dosyası oluşturulmadı.</Empty>}
            </article>
            <article className="panel"><div className="panel-head"><div><p className="eyebrow">ENTEGRATÖR AKIŞI</p><h2>Bağlantı özeti</h2></div></div>
              <div className="health-line"><span>Aktif bağlantı</span><strong>{integratorStatus?.connections ?? 0}</strong></div>
              <div className="health-line"><span>Kuyruktaki işler</span><strong>{integratorStatus?.jobs.queued ?? 0}</strong></div>
              <div className="health-line"><span>Kısmi / hatalı</span><strong>{(integratorStatus?.jobs.partial ?? 0) + (integratorStatus?.jobs.failed ?? 0)}</strong></div>
              <button className="secondary-button" onClick={() => { setInvoiceReviewRequest(null); setSection("invoices"); }}>Faturaları aç</button>
            </article>
            <GibCompatibilityPanel data={compatibility} />
          </section>
        </>}

        {section === "taxpayers" && <section className="panel"><div className="panel-head"><div><p className="eyebrow">MÜŞTERİ PORTFÖYÜ</p><h2>{taxpayers.length} mükellef</h2></div>{canPrepare && <button className="primary-button compact" onClick={() => { setEditingTaxpayerId(null); setTaxpayerForm(!taxpayerForm); }}>{taxpayerForm ? "Formu kapat" : "Yeni mükellef"}</button>}</div>
          {taxpayerForm && <TaxpayerCreateForm providers={providers} onSaved={() => { setTaxpayerForm(false); void load(); }} />}
          {editingTaxpayerId && <TaxpayerEditForm providers={providers} taxpayerId={editingTaxpayerId} onCancel={() => setEditingTaxpayerId(null)} onSaved={() => { setEditingTaxpayerId(null); void load(); }} />}
          {taxpayers.length ? <div className="table-list">{taxpayers.map((item) => <div className="table-row taxpayer-row" key={item.id}><span className="avatar">{item.title.slice(0, 2).toUpperCase()}</span><span><strong>{item.title}</strong><small>VKN {item.vkn} · {item.integrator?.provider_label || "Entegratör bağlı değil"}</small></span><span className="taxpayer-actions"><em className={`pill ${item.integrator?.enabled && item.integrator.credentials_ready ? "active" : "partial"}`}>{item.integrator?.enabled && item.integrator.credentials_ready ? "Bağlı" : "Bağlantı eksik"}</em>{canPrepare && <><button className="text-button" onClick={() => { setTaxpayerForm(false); setEditingTaxpayerId(item.id); }}>Düzenle</button><button className="text-button danger" disabled={deletingTaxpayer !== null} onClick={() => void deleteTaxpayer(item)}>Sil</button></>}</span></div>)}</div> : <Empty>Henüz mükellef oluşturmadınız.</Empty>}
        </section>}

        {section === "invoices" && <InvoicesPage taxpayers={taxpayers} jobs={jobs} status={integratorStatus} reviewRequest={invoiceReviewRequest} onCloseReview={() => setInvoiceReviewRequest(null)} onReturnToCase={() => { setInvoiceReviewRequest(null); setSection("refunds"); }} onPreview={setActiveInvoice} canPrepare={canPrepare} onQueued={load} onRetry={retryJob} onDelete={deleteJob} />}

        {section === "refunds" && activeCase && (
          <RefundCaseWorkspace key={`${activeCase.id}-${debugImportVersion}`}
            refundCase={activeCase}
            canPrepare={canPrepare}
            canApprove={canApprove}
            onBack={() => { setActiveCase(null); void load(); }}
            onReviewInvoices={(request) => {
              setInvoiceReviewRequest(request);
              setSection("invoices");
            }}
          />
        )}

        {section === "refunds" && !activeCase && <section className="panel"><div className="panel-head"><div><p className="eyebrow">KDV İADE OPERASYONU</p><h2>İade dosyaları</h2></div>{canPrepare && <button className="primary-button compact" disabled={!taxpayers.length} onClick={() => setCaseForm(!caseForm)}>Yeni dosya</button>}</div>
          {caseForm && taxRules && <RefundCaseForm taxpayers={taxpayers} taxRules={taxRules} onSaved={() => { setCaseForm(false); load(); }} />}
          <div className="refund-portfolio-filters"><label>Mükellef<select value={caseTaxpayerFilter} onChange={(event) => setCaseTaxpayerFilter(event.target.value)}><option value="all">Tüm mükellefler</option>{taxpayers.map((item) => <option value={item.id} key={item.id}>{item.vkn} · {item.title}</option>)}</select></label><label>Durum<select value={caseStatusFilter} onChange={(event) => setCaseStatusFilter(event.target.value)}><option value="open">Açık dosyalar</option><option value="all">Tüm durumlar</option><option value="draft">Hazırlanıyor</option><option value="review">Kontrolde</option><option value="ready">GİB'e hazır</option><option value="completed">Sonuçlandı</option></select></label><em className="pill active">{visibleCases.length} dosya</em></div>
          {visibleCases.length ? <div className="table-list">{visibleCases.map((item) => <div className="case-list-row" key={item.id}><button className="table-row case-open" onClick={() => setActiveCase(item)}><span><strong>{item.title}</strong><small>{item.taxpayer_vkn} · {item.taxpayer_title}</small></span><span><strong>{item.requested_amount === null ? "—" : formatMoney(item.requested_amount)}</strong><small>{item.transaction_code} · {item.transaction_name || item.claim_type} · {item.period_start} — {item.period_end}</small></span><em className={`pill ${item.status}`}>{statusLabels[item.status] || item.status}</em></button>{canPrepare && <button className="text-button danger case-delete" disabled={deletingCase !== null} onClick={() => void deleteRefundCase(item)}>Sil</button>}</div>)}</div> : <Empty>Filtreye uyan KDV iade dosyası bulunmuyor.</Empty>}
        </section>}

        {section === "reports" && <ReportsPage cases={cases} canPrepare={canPrepare} canApprove={canApprove} />}
        {section === "customs" && <CustomsPage taxpayers={taxpayers} cases={cases} canPrepare={canPrepare} />}

        {section === "legislation" && <LegislationUpdates changes={legislationChanges} canTaxApprove={canApprove} canTechnicalApprove={session.user.can_publish_legislation} onUpdated={load} />}

      </main>
      {activeInvoice && <InvoicePreviewModal invoice={activeInvoice} documentTypeLabel={documentTypeLabels[activeInvoice.doc_type]} showAccountingEntries onClose={() => setActiveInvoice(null)} />}
    </div></DebugTransferContext.Provider>
  );
}

type CredentialValues = Record<string, string | boolean>;

function credentialsAreComplete(definition: IntegratorDefinition | undefined, values: CredentialValues) {
  if (!definition) return false;
  return definition.fields.every((field) => {
    if (!field.required) return true;
    const value = values[field.name] ?? field.default;
    return typeof value === "string" ? value.trim() !== "" : value !== undefined && value !== null;
  });
}

function IntegratorFields({ definition, values, requireValues, onChange }: { definition?: IntegratorDefinition; values: CredentialValues; requireValues: boolean; onChange: (name: string, value: string | boolean) => void }) {
  if (!definition) return null;
  return <div className="credential-fields">
    {definition.fields.map((field) => {
      const value = values[field.name] ?? field.default ?? "";
      if (field.input_type === "checkbox") {
        return <label className="credential-checkbox" key={field.name}><input type="checkbox" checked={Boolean(value)} onChange={(event) => onChange(field.name, event.target.checked)} /><span>{field.label}</span></label>;
      }
      if (field.input_type === "select") {
        return <label key={field.name}>{field.label}<select required={requireValues && field.required} value={String(value)} onChange={(event) => onChange(field.name, event.target.value)}>{field.options.map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}</select>{field.help_text && <small>{field.help_text}</small>}</label>;
      }
      return <label key={field.name}>{field.label}<input required={requireValues && field.required} type={field.input_type === "password" ? "text" : field.input_type} autoComplete="off" value={String(value)} onChange={(event) => onChange(field.name, event.target.value)} />{field.help_text && <small>{field.help_text}</small>}</label>;
    })}
  </div>;
}

function TaxpayerCreateForm({ providers, onSaved }: { providers: IntegratorDefinition[]; onSaved: () => void | Promise<void> }) {
  const [form, setForm] = useState({ title: "", vkn: "", integrator_provider: "" });
  const [credentials, setCredentials] = useState<CredentialValues>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api<Taxpayer>("/api/taxpayers", {
        method: "POST",
        body: JSON.stringify({ ...form, integrator_credentials: credentials, integrator_enabled: connectionReady })
      });
      await onSaved();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Mükellef kaydedilemedi.");
    } finally {
      setBusy(false);
    }
  }

  const definition = providers.find((provider) => provider.code === form.integrator_provider);
  const connectionReady = credentialsAreComplete(definition, credentials);
  return <form className="taxpayer-form" onSubmit={submit}>
    <label>Mükellef tam resmî unvanı<input required value={form.title} onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))} /></label>
    <label>Mükellef VKN<input required inputMode="numeric" pattern="[0-9]{10}" minLength={10} maxLength={10} value={form.vkn} onChange={(event) => setForm((current) => ({ ...current, vkn: event.target.value.replace(/\D/g, "") }))} /></label>
    <label>Özel entegratör<select value={form.integrator_provider} onChange={(event) => { setForm((current) => ({ ...current, integrator_provider: event.target.value })); setCredentials({}); }}><option value="">Bağlantı yok</option>{providers.map((provider) => <option value={provider.code} key={provider.code}>{provider.label}</option>)}</select></label>
    <IntegratorFields definition={definition} values={credentials} requireValues={Boolean(definition)} onChange={(name, value) => setCredentials((current) => ({ ...current, [name]: value }))} />
    {definition && <label className="credential-checkbox"><input type="checkbox" checked={connectionReady} disabled /><span>Entegratör bağlantısı aktif</span></label>}
    <button className="primary-button compact" disabled={busy}>{busy ? "Kaydediliyor…" : "Mükellefi kaydet"}</button>
    {error && <div className="form-error full">{error}</div>}
  </form>;
}

function TaxpayerEditForm({ providers, taxpayerId, onSaved, onCancel }: { providers: IntegratorDefinition[]; taxpayerId: number; onSaved: () => void | Promise<void>; onCancel: () => void }) {
  const [detail, setDetail] = useState<TaxpayerDetail | null>(null);
  const [form, setForm] = useState({ title: "", vkn: "", integrator_provider: "" });
  const [credentials, setCredentials] = useState<CredentialValues>({});
  const [returnAccounts, setReturnAccounts] = useState("");
  const [credentialsEdited, setCredentialsEdited] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setDetail(null);
    setError("");
    api<TaxpayerDetail>(`/api/taxpayers/${taxpayerId}`).then((taxpayer) => {
      if (!active) return;
      setDetail(taxpayer);
      setReturnAccounts((taxpayer.purchase_return_accounts || []).join(", "));
      setForm({
        title: taxpayer.title,
        vkn: taxpayer.vkn,
        integrator_provider: taxpayer.integrator?.provider || ""
      });
      setCredentials(
        taxpayer.integrator_connections.find((connection) => connection.enabled)?.credentials || {}
      );
      setCredentialsEdited(false);
    }).catch((reason) => {
      if (active) setError(reason instanceof Error ? reason.message : "Mükellef bilgileri alınamadı.");
    });
    return () => { active = false; };
  }, [taxpayerId]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api<TaxpayerDetail>(`/api/taxpayers/${taxpayerId}`, {
        method: "PATCH",
        body: JSON.stringify({ ...form, purchase_return_accounts: returnAccounts.split(/[,;\n]/).map(code => code.trim()).filter(Boolean), integrator_credentials: credentials, integrator_enabled: connectionReady })
      });
      await onSaved();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Mükellef güncellenemedi.");
    } finally {
      setBusy(false);
    }
  }

  if (!detail && !error) return <div className="taxpayer-form-loading">Mükellef bilgileri yükleniyor…</div>;
  const definition = providers.find((provider) => provider.code === form.integrator_provider);
  const storedConnection = detail?.integrator_connections.find((connection) => connection.provider === form.integrator_provider);
  const connectionReady = credentialsAreComplete(definition, credentials)
    && Boolean(storedConnection?.enabled || credentialsEdited);
  return <form className="taxpayer-form taxpayer-edit-form" onSubmit={submit}>
    <div className="taxpayer-edit-head"><div><strong>Mükellefi düzenle</strong></div><button type="button" className="text-button" onClick={onCancel}>Kapat</button></div>
    <label>Mükellef tam resmî unvanı<input required value={form.title} onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))} /></label>
    <label>Mükellef VKN<input required inputMode="numeric" pattern="[0-9]{10}" minLength={10} maxLength={10} value={form.vkn} onChange={(event) => setForm((current) => ({ ...current, vkn: event.target.value.replace(/\D/g, "") }))} /></label>
    <label>Özel entegratör<select value={form.integrator_provider} onChange={(event) => { const provider = event.target.value; const connection = detail?.integrator_connections.find((item) => item.provider === provider); setForm((current) => ({ ...current, integrator_provider: provider })); setCredentials(connection?.credentials || {}); setCredentialsEdited(false); }}><option value="">Bağlantı yok</option>{providers.map((provider) => <option value={provider.code} key={provider.code}>{provider.label}</option>)}</select></label>
    <label>Alış iadelerinin kaydedildiği tam hesap kodları<input value={returnAccounts} placeholder="150.01, 153.02" onChange={event => setReturnAccounts(event.target.value)} /><small>Virgülle ayırın. Alt hesaplar otomatik kapsanmaz. Aynı fişte 391 alacak ile birlikte kontrol edilir.</small></label>
    <IntegratorFields definition={definition} values={credentials} requireValues={Boolean(definition)} onChange={(name, value) => { setCredentials((current) => ({ ...current, [name]: value })); setCredentialsEdited(true); }} />
    {definition && <label className="credential-checkbox"><input type="checkbox" checked={connectionReady} disabled /><span>Entegratör bağlantısı aktif</span></label>}
    <div className="taxpayer-edit-actions"><button type="button" className="secondary-button compact" onClick={onCancel}>Vazgeç</button><button className="primary-button compact" disabled={busy}>{busy ? "Kaydediliyor…" : "Değişiklikleri kaydet"}</button></div>
    {error && <div className="form-error full">{error}</div>}
  </form>;
}

const monthLabels = [
  "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"
];

function InvoiceLookup({ taxpayers, onPreview }: { taxpayers: Taxpayer[]; onPreview: (invoice: Invoice) => void }) {
  const [number, setNumber] = useState("");
  const [searchedNumber, setSearchedNumber] = useState("");
  const [results, setResults] = useState<Invoice[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function search(query: string, nextPage = 1) {
    if (!query.trim() || busy) return;
    setBusy(true); setError("");
    try {
      const params = new URLSearchParams({ invoice_no: query.trim(), page: String(nextPage) });
      const response = await api<{ results: Invoice[]; count: number }>(`/api/invoices/?${params}`);
      setResults(response.results); setCount(response.count); setPage(nextPage); setSearchedNumber(query.trim());
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Faturalar aranamadı.");
    } finally { setBusy(false); }
  }

  return <article className="panel invoice-lookup">
    <div className="panel-head"><h2>Fatura Görüntüle</h2></div>
    <form className="entry-form" onSubmit={event => { event.preventDefault(); void search(number); }}>
      <label>Fatura numarası<input required value={number} onChange={event => setNumber(event.target.value)} placeholder="Fatura numarasının tamamı veya bir bölümü" /></label>
      <button className="primary-button" disabled={busy || !number.trim()}>{busy ? "Aranıyor…" : "Ara"}</button>
    </form>
    {error && <p className="form-error" role="alert">{error}</p>}
    {searchedNumber && !error && <p role="status">{count ? `${count} eşleşme bulundu.` : "Bu numarayla eşleşen fatura bulunamadı."}</p>}
    <div className="invoice-lookup-results">{results.map(invoice => {
      const taxpayer = taxpayers.find(item => item.id === invoice.taxpayer);
      return <div className="invoice-lookup-row" key={invoice.id}>
        <span><strong>{invoice.invoice_no}</strong><small>{taxpayer?.title || `Mükellef #${invoice.taxpayer}`}</small><small>{invoice.issue_date || "Tarih yok"} · {invoice.direction === "in" ? "Gelen / Alış" : "Giden / Satış"}</small></span>
        <button type="button" className="text-button" onClick={() => onPreview(invoice)}>Gör</button>
      </div>;
    })}</div>
    {count > 100 && <div className="invoice-lookup-pagination">
      <button type="button" className="secondary-button compact" disabled={busy || page === 1} onClick={() => void search(searchedNumber, page - 1)}>Önceki</button>
      <span>{page} / {Math.ceil(count / 100)}</span>
      <button type="button" className="secondary-button compact" disabled={busy || page * 100 >= count} onClick={() => void search(searchedNumber, page + 1)}>Sonraki</button>
    </div>}
  </article>;
}

function InvoicesPage({ taxpayers, jobs, status, reviewRequest, onCloseReview, onReturnToCase, onPreview, canPrepare, onQueued, onRetry, onDelete }: {
  taxpayers: Taxpayer[];
  jobs: InvoiceSyncJob[];
  status: IntegratorStatus | null;
  reviewRequest: InvoiceReviewRequest | null;
  onCloseReview: () => void;
  onReturnToCase: () => void;
  onPreview: (invoice: Invoice) => void;
  canPrepare: boolean;
  onQueued: () => void | Promise<void>;
  onRetry: (id: number) => void | Promise<void>;
  onDelete: (job: InvoiceSyncJob) => void | Promise<void>;
}) {
  const today = new Date();
  const [taxpayerId, setTaxpayerId] = useState(String(taxpayers[0]?.id || ""));
  const [year, setYear] = useState(String(today.getFullYear()));
  const [month, setMonth] = useState(String(today.getMonth() + 1));
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invoicePage, setInvoicePage] = useState(1);
  const [invoiceCount, setInvoiceCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [deletingPeriod, setDeletingPeriod] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);
  const [issueJobId, setIssueJobId] = useState<number | null>(null);
  const [jobDetailMode, setJobDetailMode] = useState<"issues" | "warnings" | null>(null);
  const [issueInvoices, setIssueInvoices] = useState<Invoice[]>([]);
  const [issueLoading, setIssueLoading] = useState(false);
  const [issueMessage, setIssueMessage] = useState("");
  const [reviewInvoices, setReviewInvoices] = useState<Invoice[]>([]);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewMessage, setReviewMessage] = useState("");
  const [refreshingInvoiceId, setRefreshingInvoiceId] = useState<number | null>(null);
  const [excludingInvoiceId, setExcludingInvoiceId] = useState<number | null>(null);
  const [excludingAllReviewInvoices, setExcludingAllReviewInvoices] = useState(false);
  const reviewPanelRef = useRef<HTMLElement | null>(null);
  const issueListRef = useRef<HTMLDivElement | null>(null);
  const [issueScrollEdges, setIssueScrollEdges] = useState({ left: false, right: false });
  const years = useMemo(() => Array.from({ length: 20 }, (_, index) => today.getFullYear() - index), [today]);

  const updateIssueScrollEdges = useCallback(() => {
    const list = issueListRef.current;
    if (!list) {
      setIssueScrollEdges({ left: false, right: false });
      return;
    }
    const tolerance = 2;
    setIssueScrollEdges({
      left: list.scrollLeft > tolerance,
      right: list.scrollLeft + list.clientWidth < list.scrollWidth - tolerance
    });
  }, []);

  useEffect(() => {
    if (!taxpayers.some((item) => String(item.id) === taxpayerId)) {
      setTaxpayerId(String(taxpayers[0]?.id || ""));
      setInvoicePage(1);
    }
  }, [taxpayers, taxpayerId]);

  useEffect(() => {
    if (!taxpayerId) {
      setInvoices([]);
      setInvoiceCount(0);
      return;
    }
    const controller = new AbortController();
    const numericYear = Number(year);
    const numericMonth = Number(month);
    const lastDay = new Date(numericYear, numericMonth, 0).getDate();
    const startDate = `${year}-${month.padStart(2, "0")}-01`;
    const endDate = `${year}-${month.padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
    const query = new URLSearchParams({ taxpayer: taxpayerId, start_date: startDate, end_date: endDate, page: String(invoicePage) });
    setLoading(true);
    setError("");
    api<{ count: number; results: Invoice[] }>(`/api/invoices/?${query}`, { signal: controller.signal })
      .then((result) => { setInvoices(result.results); setInvoiceCount(result.count); })
      .catch((reason) => { if (reason instanceof Error && reason.name !== "AbortError") { setInvoices([]); setInvoiceCount(0); setError(reason.message); } })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [taxpayerId, year, month, refreshToken, invoicePage]);

  useEffect(() => {
    if (!reviewRequest?.invoiceIds.length) {
      setReviewInvoices([]);
      setReviewMessage("");
      return;
    }
    const controller = new AbortController();
    const order = new Map(reviewRequest.invoiceIds.map((id, index) => [id, index]));
    setReviewLoading(true);
    setReviewMessage("");
    api<Invoice[]>(`/api/invoices/?ids=${reviewRequest.invoiceIds.join(",")}`, { signal: controller.signal })
      .then((items) => setReviewInvoices([...items].sort((left, right) => (order.get(left.id) ?? 0) - (order.get(right.id) ?? 0))))
      .catch((reason) => {
        if (reason instanceof Error && reason.name !== "AbortError") setReviewMessage(reason.message);
      })
      .finally(() => { if (!controller.signal.aborted) setReviewLoading(false); });
    const frame = window.requestAnimationFrame(() => reviewPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
    return () => {
      controller.abort();
      window.cancelAnimationFrame(frame);
    };
  }, [reviewRequest]);

  useEffect(() => {
    const list = issueListRef.current;
    if (!list) {
      setIssueScrollEdges({ left: false, right: false });
      return;
    }
    const frame = window.requestAnimationFrame(updateIssueScrollEdges);
    const observer = new ResizeObserver(updateIssueScrollEdges);
    observer.observe(list);
    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [issueInvoices, issueJobId, issueLoading, updateIssueScrollEdges]);

  async function showJobIssues(job: InvoiceSyncJob, force = false) {
    if (!force && issueJobId === job.id && jobDetailMode === "issues") {
      setIssueJobId(null);
      setJobDetailMode(null);
      setIssueInvoices([]);
      setIssueMessage("");
      return;
    }
    setIssueJobId(job.id);
    setJobDetailMode("issues");
    setIssueLoading(true);
    setIssueMessage("");
    try {
      setIssueInvoices(await api<Invoice[]>(`/api/invoices/?sync_job=${job.id}&review_required=true`));
    } catch (reason) {
      setIssueMessage(reason instanceof Error ? reason.message : "Sorunlu belgeler yüklenemedi.");
    } finally {
      setIssueLoading(false);
    }
  }

  async function showJobWarnings(job: InvoiceSyncJob) {
    if (issueJobId === job.id && jobDetailMode === "warnings") {
      setIssueJobId(null);
      setJobDetailMode(null);
      setIssueInvoices([]);
      setIssueMessage("");
      return;
    }
    setIssueJobId(job.id);
    setJobDetailMode("warnings");
    setIssueLoading(true);
    setIssueMessage("");
    try {
      setIssueInvoices(await api<Invoice[]>(`/api/invoices/?sync_job=${job.id}&analysis_warning=true`));
    } catch (reason) {
      setIssueMessage(reason instanceof Error ? reason.message : "Otomatik yorumlar yüklenemedi.");
    } finally {
      setIssueLoading(false);
    }
  }

  async function refreshInvoiceSource(invoice: Invoice, target: "job" | "review" = "job") {
    setRefreshingInvoiceId(invoice.id);
    if (target === "review") setReviewMessage("");
    else setIssueMessage("");
    try {
      const queued = await api<InvoiceSyncJob>(`/api/invoices/${invoice.id}/refresh-source`, { method: "POST" });
      await onQueued();
      const message = `${invoice.invoice_no || invoice.uuid || `Fatura #${invoice.id}`} için yeniden getirme işlemi #${queued.id} kuyruğa alındı. İşlem bitince iade dosyasındaki kontrol listesini yenileyin.`;
      if (target === "review") setReviewMessage(message);
      else setIssueMessage(message);
    } catch (reason) {
      const message = reason instanceof Error ? reason.message : "Kaynak belge yeniden getirilemedi.";
      if (target === "review") setReviewMessage(message);
      else setIssueMessage(message);
    } finally {
      setRefreshingInvoiceId(null);
    }
  }

  async function excludeInvoiceFromCase(invoice: Invoice) {
    const label = invoice.invoice_no || invoice.uuid || `Fatura #${invoice.id}`;
    const confirmed = window.confirm(
      `${label} yalnız bu KDV iade dosyasının hesabına alınmayacak. Fatura kaydı silinmeyecek. Devam edilsin mi?`
    );
    if (!confirmed || !reviewRequest) return;
    setExcludingInvoiceId(invoice.id);
    setReviewMessage("");
    try {
      await api(`/api/refund-cases/${reviewRequest.refundCaseId}/invoice-exclusions`, {
        method: "POST",
        body: JSON.stringify({
          invoice_id: invoice.id,
          reason_code: reviewRequest.code,
          explanation: "Kaynak belge yeniden getirildiği halde sorun çözülemedi; bu iade dosyasının kapsamı dışında bırakıldı."
        })
      });
      await refreshRefundPreparation(reviewRequest.refundCaseId);
      setReviewInvoices((items) => items.filter((item) => item.id !== invoice.id));
      setReviewMessage(`${label} bu iade dosyasına dahil edilmeyecek. Zorunlu kontroller yenilendi.`);
    } catch (reason) {
      setReviewMessage(reason instanceof Error ? reason.message : "Fatura kapsam dışında bırakılamadı.");
    } finally {
      setExcludingInvoiceId(null);
    }
  }

  async function refreshRefundPreparation(refundCaseId: number) {
    let job = await api<RefundWorkJob<AllocationPreparation>>(
      `/api/refund-cases/${refundCaseId}/prepare-allocations`,
      { method: "POST", headers: { Prefer: "respond-async" } }
    );
    while (job.status === "queued" || job.status === "running") {
      await new Promise((resolve) => window.setTimeout(resolve, 500));
      try {
        job = await api<RefundWorkJob<AllocationPreparation>>(`/api/refund-work-jobs/${job.id}`);
      } catch (reason) {
        if (reason instanceof ApiError && reason.status < 500) throw reason;
        continue;
      }
    }
    if (job.status === "failed") throw new Error(job.error || "Zorunlu kontroller yenilenemedi.");
  }

  async function excludeAllReviewInvoicesFromCase() {
    if (!reviewRequest || ![
      "foreign-currency-purchases",
      "invoice-download-errors",
      "unparsed-invoices"
    ].includes(reviewRequest.code) || !reviewInvoices.length) return;
    const invoiceLabel = reviewRequest.code === "foreign-currency-purchases"
      ? "dövizli alış faturası"
      : "sorunlu fatura";
    const confirmed = window.confirm(
      `${reviewInvoices.length} ${invoiceLabel} yalnız bu KDV iade dosyasının hesabına alınmayacak. Faturalar silinmeyecek. Devam edilsin mi?`
    );
    if (!confirmed) return;
    setExcludingAllReviewInvoices(true);
    setReviewMessage("");
    const failedIds = new Set<number>();
    for (const invoice of reviewInvoices) {
      try {
        await api(`/api/refund-cases/${reviewRequest.refundCaseId}/invoice-exclusions`, {
          method: "POST",
          body: JSON.stringify({
            invoice_id: invoice.id,
            reason_code: reviewRequest.code,
            explanation: reviewRequest.code === "foreign-currency-purchases"
              ? "Doğrulanmış TL karşılığı bulunmadığı için bu iade dosyasının kapsamı dışında bırakıldı."
              : "Kaynak belge yeniden getirilemediği için bu iade dosyasının kapsamı dışında bırakıldı."
          })
        });
      } catch {
        failedIds.add(invoice.id);
      }
    }
    const completedCount = reviewInvoices.length - failedIds.size;
    if (completedCount > 0) {
      try {
        await refreshRefundPreparation(reviewRequest.refundCaseId);
      } catch (reason) {
        setReviewInvoices((items) => items.filter((invoice) => failedIds.has(invoice.id)));
        const refreshError = reason instanceof Error
          ? reason.message
          : "Zorunlu kontroller yenilenemedi.";
        setReviewMessage(
          `${completedCount} fatura kapsam dışında bırakıldı; kontroller yenilenemedi: ${refreshError}`
        );
        setExcludingAllReviewInvoices(false);
        return;
      }
    }
    setReviewInvoices((items) => items.filter((invoice) => failedIds.has(invoice.id)));
    setReviewMessage(failedIds.size
      ? `${completedCount} fatura kapsam dışında bırakıldı; ${failedIds.size} fatura bırakılamadı.`
      : `${completedCount} fatura bu iade dosyasının kapsamı dışında bırakıldı. Zorunlu kontroller yenilendi.`);
    setExcludingAllReviewInvoices(false);
  }

  async function deletePeriodInvoices(scope: "month" | "year") {
    if (!taxpayerId || deletingPeriod) return;
    const taxpayer = taxpayers.find((item) => String(item.id) === taxpayerId);
    const period = scope === "year" ? `${year} yılının tamamı` : `${year} ${monthLabels[Number(month) - 1]}`;
    const linkNotice = scope === "year" ? "\nSilinen faturalara olan fatura bağlantıları da kaldırılacak. Başka yılların faturaları silinmeyecek." : "";
    if (!window.confirm(`${taxpayer?.vkn} · ${taxpayer?.title}\nSeçilen dönem (${period}) için gelen ve giden tüm faturalar kalıcı olarak silinsin mi?${linkNotice}\nBu işlem geri alınamaz. Hesaplama veya muhasebede kullanılan faturalar varsa silme yapılmaz.`)) return;
    setDeletingPeriod(true);
    setError("");
    setDeleteMessage("");
    try {
      const result = await api<{ deleted_invoice_count: number }>("/api/invoices/period", {
        method: "DELETE", body: JSON.stringify({ taxpayer: Number(taxpayerId), year: Number(year), scope, ...(scope === "month" ? { month: Number(month) } : {}) })
      });
      setDeleteMessage(`${period}: ${result.deleted_invoice_count} fatura silindi.`);
      setInvoicePage(1);
      setRefreshToken((value) => value + 1);
      await onQueued();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Faturalar silinemedi.");
    } finally {
      setDeletingPeriod(false);
    }
  }

  const activeJobs = jobs.filter((job) => job.status === "queued" || job.status === "running").length;
  const reviewInstruction = reviewRequest?.instruction || (reviewRequest?.code === "unparsed-invoices"
    ? "Önce faturayı yeniden getirin. Aynı sorun devam ederse “Bu iade dosyasına dahil etme”ye basın ve ilerleyin."
    : reviewRequest?.code === "invoice-download-errors"
      ? "Önce faturayı yeniden getirin. Aynı hata devam ederse “Bu iade dosyasına dahil etme”ye basın ve ilerleyin."
      : reviewRequest?.code === "foreign-currency-purchases"
        ? "Bu faturaların doğrulanmış TL karşılığı yok. Bu iade dosyasında kullanılmayacak faturaları “Bu iade dosyasına dahil etme” ile kapsam dışında bırakın. Fatura kaydı silinmez."
      : "Aşağıdaki faturaları ve açıklamalarını kontrol edin. Eksik belgeyi yeniden getirin.");
  return <>{reviewRequest && <section className="panel invoice-resolution-panel" ref={reviewPanelRef}>
    <div className="panel-head"><div><p className="eyebrow">DOSYA KONTROL LİSTESİNDEN AÇILDI</p><h2>{reviewRequest.title}</h2></div><div className="invoice-resolution-head-actions"><button className="secondary-button compact" type="button" disabled={excludingAllReviewInvoices} onClick={onCloseReview}>Tüm faturaları göster</button><button className="primary-button compact" type="button" disabled={excludingAllReviewInvoices} onClick={onReturnToCase}>İade dosyasına dön</button></div></div>
    <div className="notice invoice-resolution-instruction"><strong>Yapılacak işlem</strong><span>{reviewInstruction}</span></div>
    {["foreign-currency-purchases", "invoice-download-errors", "unparsed-invoices"].includes(reviewRequest.code) && reviewInvoices.length > 0 && canPrepare && <div className="invoice-resolution-bulk-action"><span><strong>{reviewInvoices.length} sorunlu fatura</strong><small>Tamamını yalnız bu iade dosyasının dışında bırakabilirsiniz. Fatura kayıtları silinmez.</small></span><button className="primary-button compact" type="button" disabled={excludingAllReviewInvoices} onClick={() => void excludeAllReviewInvoicesFromCase()}>{excludingAllReviewInvoices ? "Kapsam dışında bırakılıyor…" : "Tüm sorunlu faturaları bu iade dosyasına dahil etme"}</button></div>}
    {reviewMessage && <div className="notice invoice-resolution-message">{reviewMessage}</div>}
    {reviewLoading ? <div className="invoice-loading">Sorunlu faturalar yükleniyor…</div> : reviewInvoices.length ? <div className="table-list invoice-resolution-list">{reviewInvoices.map((invoice) => {
      const reason = reviewRequest.code === "unparsed-invoices"
        ? "Fatura satırları alınmamış. Entegratörden yeniden getirin."
        : reviewRequest.code === "foreign-currency-purchases"
          ? `${invoice.currency || "Döviz"} cinsindeki belgenin doğrulanmış TL karşılığı bulunmuyor.`
        : reviewRequest.code.startsWith("loading-")
          ? reviewRequest.instruction || "Fatura ve muhasebe kaydını kontrol edin."
        : invoice.review_explanation || invoice.download_error || "Faturanın kaynak bilgisi eksik. Entegratörden yeniden getirin.";
      return <div className="table-row invoice-issue-row invoice-resolution-row" key={invoice.id}>
        <span><strong>{invoice.invoice_no || invoice.uuid || `Fatura #${invoice.id}`}</strong><small>{invoice.sender_name || "Satıcı bilgisi yok"} · {invoice.issue_date || "Tarih yok"} · {documentTypeLabels[invoice.doc_type]} · {invoice.direction === "in" ? "Gelen" : "Giden"}</small></span>
        <span><strong>{reason}</strong>{invoice.download_error && <small className="technical-detail" title={invoice.download_error}>Teknik ayrıntı: {invoice.download_error}</small>}</span>
        <span className="invoice-issue-actions">
          {invoice.has_artifact && <button className="text-button" type="button" onClick={() => onPreview(invoice)}>Faturayı görüntüle</button>}
          {invoice.source_xml_available && <a className="text-button" href={`/api/invoices/${invoice.id}/source-artifact`}>Kaynak XML'i indir</a>}
          {canPrepare && ["invoice-download-errors", "unparsed-invoices"].includes(reviewRequest.code) && <button className="primary-button compact" type="button" disabled={excludingAllReviewInvoices || refreshingInvoiceId === invoice.id} onClick={() => void refreshInvoiceSource(invoice, "review")}>{refreshingInvoiceId === invoice.id ? "Kuyruğa alınıyor…" : "Entegratörden yeniden getir"}</button>}
          {canPrepare && ["foreign-currency-purchases", "invoice-download-errors", "unparsed-invoices"].includes(reviewRequest.code) && <button className="secondary-button compact invoice-exclude-button" type="button" disabled={excludingAllReviewInvoices || excludingInvoiceId === invoice.id} onClick={() => void excludeInvoiceFromCase(invoice)}>{excludingInvoiceId === invoice.id ? "Hariç bırakılıyor…" : "Bu iade dosyasına dahil etme"}</button>}
        </span>
      </div>;
    })}</div> : <Empty>Kontrol listesindeki faturalar artık bulunmuyor. İade dosyasına dönüp durumu yenileyin.</Empty>}
  </section>}<section className="panel-grid invoice-operations">
    <div className="invoice-operations-left"><article className="panel"><div className="panel-head"><div><p className="eyebrow">ENTEGRATÖR OPERASYONU</p><h2>Faturaları çek</h2></div></div>
      {canPrepare ? <InvoicePullForm taxpayers={taxpayers} onQueued={onQueued} /> : <div className="notice">Direktör rolünde faturalar yalnızca incelenebilir.</div>}
      <div className="health-line"><span>Aktif entegratör bağlantısı</span><strong>{status?.connections ?? 0}</strong></div>
      <div className="health-line"><span>Toplam fatura</span><strong>{status?.invoices ?? 0}</strong></div>
    </article><InvoiceLookup taxpayers={taxpayers} onPreview={onPreview} /></div>
    <article className="panel span-two"><div className="panel-head"><div><p className="eyebrow">FATURA ÇEKME GEÇMİŞİ</p><h2>Son işlemler</h2></div>{activeJobs > 0 && <em className="pill running">Otomatik güncelleniyor</em>}</div>
      {jobs.length ? <div className="table-list">{jobs.slice(0, 10).map((job) => <div className="job-history-item" key={job.id}>
        <div className="table-row job-row">
          <span>
            <strong>{job.taxpayer_vkn} · {job.taxpayer_title}</strong>
            <small>{job.start_date} — {job.end_date} · {job.doc_types.join(", ")} · {directionLabel(job.directions)} · deneme {job.attempts}</small>
            {job.window_summary.retryable > 0 && job.error && <small className="job-error" title={job.error}>{job.error}</small>}
          </span>
          <span className="job-progress">
            <small><strong>{job.window_summary.completed}/{job.window_summary.total}</strong> sorgu tamamlandı</small>
            <small><strong>{job.document_summary.verified}</strong> fatura hazır · <strong>{job.document_summary.issues}</strong> inceleme gerekli{job.document_summary.warnings > 0 && <> · <strong>{job.document_summary.warnings}</strong> fatura otomatik yorumlandı</>}</small>
          </span>
          <span className="job-actions">
            <em className={`pill ${job.document_summary.issues > 0 && job.window_summary.retryable === 0 && job.status !== "running" ? "partial" : job.status}`}>{job.document_summary.issues > 0 && job.window_summary.retryable === 0 && job.status !== "running" ? "İnceleme gerekli" : statusLabels[job.status] || job.status}</em>
            <span className="job-action-buttons">
              {job.document_summary.issues > 0 && <button type="button" className="job-action-button" onClick={() => void showJobIssues(job)}>{issueJobId === job.id && jobDetailMode === "issues" ? "İncelemeyi kapat" : "İncelenecek belgeleri göster"}</button>}
              {job.document_summary.warnings > 0 && <button type="button" className="job-action-button" onClick={() => void showJobWarnings(job)}>{issueJobId === job.id && jobDetailMode === "warnings" ? "Yorumları kapat" : "Otomatik yorumları göster"}</button>}
              {canPrepare && job.window_summary.retryable > 0 && (job.status === "failed" || job.status === "partial") && <button type="button" className="job-action-button retry" onClick={() => void onRetry(job.id)}>Geçici hataları tekrar dene</button>}
              {canPrepare && job.status !== "queued" && job.status !== "running" && <button type="button" className="job-action-button danger" onClick={() => void onDelete(job)}>Sil</button>}
            </span>
          </span>
        </div>
        {issueJobId === job.id && <div className="invoice-issue-panel">
          <div className="notice invoice-review-notice">{jobDetailMode === "warnings"
            ? "Bu faturalar başarıyla işlendi. Sistem kaynak XML'deki eksik veya farklı gösterilen alanları aşağıdaki şekilde yorumladı."
            : "Bu belgeler “hatalı fatura” olarak kesinleştirilmedi; yalnızca KDV hesabına güvenle alınamadı. Kaynak XML'i indirebilir, düzenleyen taraf veya entegratörde düzeltildikten sonra yeniden getirebilirsiniz."}</div>
          {issueMessage && <div className="notice">{issueMessage}</div>}
          {issueLoading ? <div className="invoice-loading">{jobDetailMode === "warnings" ? "Otomatik yorumlar yükleniyor…" : "İnceleme kayıtları yükleniyor…"}</div> : issueInvoices.length ? <div className={`invoice-issue-scroll-shell ${issueScrollEdges.left ? "can-scroll-left" : ""} ${issueScrollEdges.right ? "can-scroll-right" : ""}`}><div className="table-list invoice-issue-scroll-list" ref={issueListRef} onScroll={updateIssueScrollEdges}>{issueInvoices.map((invoice) => <div className="table-row invoice-issue-row" key={invoice.id}>
            <span>
              <strong>{invoice.invoice_no || invoice.uuid || `Fatura #${invoice.id}`}</strong>
              <small>{invoice.issue_date || "Tarih yok"} · {documentTypeLabels[invoice.doc_type]} · {invoice.direction === "in" ? "Gelen" : "Giden"}</small>
            </span>
            {jobDetailMode === "warnings" ? <span className="invoice-analysis-comments">{invoice.analysis_warnings.map((warning) => <strong key={warning}>{warning}</strong>)}</span> : <span>
                <strong>{invoice.review_explanation}</strong>
                <small className="technical-detail" title={invoice.download_error}>Teknik ayrıntı: {invoice.download_error}</small>
              </span>}
            <span className="invoice-issue-actions">
              <em className={`pill ${jobDetailMode === "warnings" ? "active" : invoice.source_xml_available ? "partial" : "failed"}`}>{jobDetailMode === "warnings" ? "Otomatik yorumlandı" : invoice.source_xml_available ? "Kaynak XML incelenebilir" : "Kaynak XML alınamadı"}</em>
              {jobDetailMode === "warnings" && <button className="text-button" onClick={() => onPreview(invoice)}>Faturayı görüntüle</button>}
              {invoice.source_xml_available && <a className="text-button" href={`/api/invoices/${invoice.id}/source-artifact`}>Kaynak XML'i indir</a>}
              {jobDetailMode === "issues" && canPrepare && <button className="text-button" disabled={refreshingInvoiceId === invoice.id} onClick={() => void refreshInvoiceSource(invoice)}>{refreshingInvoiceId === invoice.id ? "Kuyruğa alınıyor…" : "Entegratörden yeniden getir"}</button>}
            </span>
          </div>)}</div></div> : <Empty>{jobDetailMode === "warnings" ? "Otomatik yorumlanan fatura bulunmuyor." : "İnceleme gerektiren belge kalmadı."}</Empty>}
        </div>}
      </div>)}</div> : <Empty>Henüz entegratör fatura çekme işlemi bulunmuyor.</Empty>}
    </article>
  </section><section className="panel invoices-page">
    <div className="panel-head"><div><p className="eyebrow">FATURA BELGELERİ</p><h2>Entegratörden veya dosyadan alınan faturalar</h2></div><div className="invoice-page-actions"><em className="pill active">{invoiceCount} kayıt</em>{canPrepare && <button className="secondary-button compact" disabled={!taxpayerId} onClick={() => setImportOpen((open) => !open)}>{importOpen ? "Yüklemeyi kapat" : "Dosya / klasör yükle"}</button>}</div></div>
    <div className="invoice-taxpayer">
      <label>Mükellef<select value={taxpayerId} disabled={deletingPeriod || !taxpayers.length} onChange={(event) => { setTaxpayerId(event.target.value); setInvoicePage(1); }}>{taxpayers.map((item) => <option value={item.id} key={item.id}>{item.vkn} · {item.title}</option>)}</select></label>
    </div>
    {canPrepare && importOpen && <InvoiceImportForm taxpayerId={taxpayerId} onImported={() => { setInvoicePage(1); setRefreshToken((value) => value + 1); }} />}
    <div className="invoice-filters">
      <label>Yıl<select disabled={deletingPeriod} value={year} onChange={(event) => { setYear(event.target.value); setInvoicePage(1); }}>{years.map((item) => <option value={item} key={item}>{item}</option>)}</select></label>
      <label>Ay<select disabled={deletingPeriod} value={month} onChange={(event) => { setMonth(event.target.value); setInvoicePage(1); }}>{monthLabels.map((label, index) => <option value={index + 1} key={label}>{label}</option>)}</select></label>
      {canPrepare && <>
      <button className="secondary-button compact" type="button" disabled={!taxpayerId || loading || deletingPeriod} onClick={() => void deletePeriodInvoices("year")}>Seçilen yılın tüm faturalarını sil</button>
      <button className="secondary-button compact" type="button" disabled={!taxpayerId || loading || deletingPeriod || !invoices.length} onClick={() => void deletePeriodInvoices("month")}>Seçilen ayın tüm faturalarını sil</button>
      </>}
    </div>
    {canPrepare && <div className="invoice-delete-actions">
      <DebugTransferControls scope="invoices" targetId={taxpayerId} disabled={loading || deletingPeriod} onImported={() => { setInvoicePage(1); setRefreshToken(value => value + 1); }} />
      {deletingPeriod && <span role="status">Siliniyor…</span>}
    </div>}
    {deleteMessage && <div className="notice">{deleteMessage}</div>}
    {error && <div className="form-error">{error}</div>}
    {loading ? <div className="invoice-loading">Faturalar yükleniyor…</div> : invoices.length ? <div className="table-list invoice-list">{invoices.map((invoice) => {
      const taxpayer = taxpayers.find((item) => item.id === invoice.taxpayer);
      const party = invoice.direction === "in" ? invoice.sender_name || invoice.sender_vkn : invoice.receiver_name || invoice.receiver_vkn;
      const analysisTitle = invoice.download_error
        ? invoice.review_explanation
        : invoice.analysis_warnings.join(" ");
      const analysisLabel = invoice.download_error
        ? "İnceleme gerekli"
        : invoice.analysis_warnings.length
          ? "Otomatik yorumlandı"
          : invoice.direction === "in" ? "Gelen" : "Giden";
      return <div className="table-row invoice-row" key={invoice.id}><span><strong>{invoice.invoice_no || invoice.uuid || `Fatura #${invoice.id}`}</strong><small>{taxpayer ? `${taxpayer.vkn} · ${taxpayer.title}` : `Mükellef #${invoice.taxpayer}`}</small></span><span><strong>{party || "Taraf bilgisi yok"}</strong><small>{invoice.issue_date || "Tarih yok"} · {documentTypeLabels[invoice.doc_type] || invoice.doc_type}</small></span><span className="invoice-total"><strong>{formatMoney(invoice.total_amount, invoice.currency)}</strong><em className={`pill ${invoice.download_error ? "failed" : invoice.analysis_warnings.length ? "partial" : "active"}`} title={analysisTitle}>{analysisLabel}</em><button className="text-button" disabled={!invoice.has_artifact} onClick={() => onPreview(invoice)}>Görüntüle</button></span></div>;
    })}</div> : <Empty>{taxpayers.length ? "Seçilen dönemde indirilmiş fatura bulunmuyor." : "Önce bir mükellef oluşturun."}</Empty>}
    {invoiceCount > 100 && <div className="invoice-page-actions" aria-label="Fatura sayfaları">
      <button className="secondary-button compact" disabled={loading || invoicePage === 1} onClick={() => setInvoicePage((page) => page - 1)}>Önceki</button>
      <span>Sayfa {invoicePage} / {Math.ceil(invoiceCount / 100)}</span>
      <button className="secondary-button compact" disabled={loading || invoicePage * 100 >= invoiceCount} onClick={() => setInvoicePage((page) => page + 1)}>Sonraki</button>
    </div>}
  </section></>;
}

function invoiceImportIssueText(issue: InvoiceImportIssue): string {
  if (typeof issue === "string") return issue;
  const location = [issue.filename || issue.file, issue.row ? `satır ${issue.row}` : ""].filter(Boolean).join(" · ");
  return [location, issue.message || issue.code || "Dosya kontrolü gerekli"].filter(Boolean).join(": ");
}

const INVOICE_UPLOAD_EXTENSIONS = new Set(["xml", "pdf", "xlsx", "zip"]);
const INVOICE_UPLOAD_BATCH_FILES = 100;
const INVOICE_UPLOAD_BATCH_BYTES = 20 * 1024 * 1024;

function supportedInvoiceFile(file: File) {
  return INVOICE_UPLOAD_EXTENSIONS.has(file.name.split(".").pop()?.toLocaleLowerCase("en-US") || "");
}

function fileFromDroppedEntry(entry: FileSystemFileEntry) {
  return new Promise<File>((resolve, reject) => entry.file(resolve, reject));
}

function directoryEntries(entry: FileSystemDirectoryEntry) {
  const reader = entry.createReader();
  return new Promise<FileSystemEntry[]>((resolve, reject) => {
    const found: FileSystemEntry[] = [];
    const readNext = () => reader.readEntries((entries) => {
      if (!entries.length) {
        resolve(found);
        return;
      }
      found.push(...entries);
      readNext();
    }, reject);
    readNext();
  });
}

async function filesFromDroppedEntry(entry: FileSystemEntry): Promise<File[]> {
  if (entry.isFile) return [await fileFromDroppedEntry(entry as FileSystemFileEntry)];
  const children = await directoryEntries(entry as FileSystemDirectoryEntry);
  return (await Promise.all(children.map(filesFromDroppedEntry))).flat();
}

async function invoiceFilesFromDrop(dataTransfer: DataTransfer) {
  const items = Array.from(dataTransfer.items || []);
  const entries = items
    .map((item) => item.webkitGetAsEntry?.() || null)
    .filter((entry): entry is FileSystemEntry => entry !== null);
  const files = entries.length
    ? (await Promise.all(entries.map(filesFromDroppedEntry))).flat()
    : Array.from(dataTransfer.files || []);
  return files.filter(supportedInvoiceFile).sort((left, right) => left.name.localeCompare(right.name, "tr"));
}

function invoiceUploadBatches(files: File[]) {
  const batches: File[][] = [];
  let current: File[] = [];
  let currentBytes = 0;
  for (const file of files) {
    if (
      current.length
      && (current.length >= INVOICE_UPLOAD_BATCH_FILES || currentBytes + file.size > INVOICE_UPLOAD_BATCH_BYTES)
    ) {
      batches.push(current);
      current = [];
      currentBytes = 0;
    }
    current.push(file);
    currentBytes += file.size;
  }
  if (current.length) batches.push(current);
  return batches;
}

function InvoiceImportForm({ taxpayerId, onImported }: { taxpayerId: string; onImported: () => void }) {
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [result, setResult] = useState<{
    status: InvoiceImportBatch["status"];
    invoice_count: number;
    line_count: number;
    error_count: number;
    warning_count: number;
    issues: InvoiceImportIssue[];
    batch_count: number;
  } | null>(null);
  const [progress, setProgress] = useState({ current: 0, total: 0, processed: 0 });
  const [error, setError] = useState("");
  const folderInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    folderInputRef.current?.setAttribute("webkitdirectory", "");
    folderInputRef.current?.setAttribute("directory", "");
  }, []);

  function selectFiles(selected: File[]) {
    setFiles(selected.filter(supportedInvoiceFile));
    setResult(null);
    setError("");
  }

  async function dropFiles(event: DragEvent<HTMLFormElement>) {
    event.preventDefault();
    event.stopPropagation();
    setDragging(false);
    setScanning(true);
    setResult(null);
    setError("");
    try {
      const selected = await invoiceFilesFromDrop(event.dataTransfer);
      setFiles(selected);
      if (!selected.length) setError("Klasörde desteklenen XML, XLSX veya ZIP fatura dosyası bulunamadı.");
    } catch (reason) {
      setFiles([]);
      setError(reason instanceof Error ? reason.message : "Klasör içeriği okunamadı.");
    } finally {
      setScanning(false);
    }
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!files.length) {
      setError("Fatura dosyalarını veya fatura klasörünü seçin.");
      return;
    }
    const direction = String(new FormData(event.currentTarget).get("direction") || "auto");
    const batches = invoiceUploadBatches(files);
    setBusy(true);
    setResult(null);
    setError("");
    setProgress({ current: 0, total: batches.length, processed: 0 });
    let importedAny = false;
    let activeBatch = 0;
    const summary = {
      status: "succeeded" as InvoiceImportBatch["status"],
      invoice_count: 0,
      line_count: 0,
      error_count: 0,
      warning_count: 0,
      issues: [] as InvoiceImportIssue[],
      batch_count: batches.length
    };
    try {
      for (let index = 0; index < batches.length; index += 1) {
        activeBatch = index + 1;
        const data = new FormData();
        data.set("taxpayer", taxpayerId);
        data.set("direction", direction);
        batches[index].forEach((file) => data.append("files", file, file.name));
        setProgress((current) => ({ ...current, current: activeBatch }));
        const imported = await api<InvoiceImportBatch>("/api/invoices/imports", {
          method: "POST",
          body: data
        });
        importedAny = true;
        summary.invoice_count += imported.invoice_count;
        summary.line_count += imported.line_count;
        summary.error_count += imported.error_count;
        summary.warning_count += imported.warning_count;
        summary.issues.push(...imported.issues.slice(0, Math.max(0, 20 - summary.issues.length)));
        if (imported.status !== "succeeded") summary.status = "partial";
        setProgress({
          current: index + 1,
          total: batches.length,
          processed: batches.slice(0, index + 1).reduce((count, batch) => count + batch.length, 0)
        });
      }
      if (summary.error_count) summary.status = summary.invoice_count ? "partial" : "failed";
      setResult(summary);
      setFiles([]);
    } catch (reason) {
      setResult(importedAny ? { ...summary, status: summary.invoice_count ? "partial" : "failed" } : null);
      setError(
        `${activeBatch || 1}/${batches.length} numaralı yükleme grubunda işlem durdu: `
        + (reason instanceof Error ? reason.message : "Faturalar içe aktarılamadı.")
        + " Daha önce tamamlanan gruplar korunur; klasörü yeniden yüklemek güvenlidir."
      );
    } finally {
      if (importedAny) await onImported();
      setBusy(false);
    }
  }

  return <form className={`invoice-import-form ${dragging ? "dragging" : ""}`} onSubmit={submit} onDragEnter={(event) => { event.preventDefault(); setDragging(true); }} onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = "copy"; }} onDragLeave={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false); }} onDrop={(event) => void dropFiles(event)}>
    <div><p className="eyebrow">DOSYADAN FATURA AKTARIMI</p><strong>Dosya veya klasörü bırakın</strong><small>Alt klasörler otomatik taranır; UBL/XML, XML ZIP, alış e-Arşiv/SMM PDF’leri ve yurt dışı hizmet şablonları güvenli gruplar halinde aktarılır.</small></div>
    <div className="invoice-file-drop">
      <strong>{scanning ? "Klasör taranıyor…" : dragging ? "Klasörü buraya bırakın" : "Fatura dosyaları / klasörü"}</strong>
      <div><label className="secondary-button compact">Dosya seç<input multiple type="file" accept=".xml,.pdf,.xlsx,.zip,application/xml,application/pdf,application/zip,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" disabled={busy || scanning} onChange={(event) => selectFiles(Array.from(event.target.files || []))} /></label><label className="secondary-button compact">Klasör seç<input ref={folderInputRef} multiple type="file" disabled={busy || scanning} onChange={(event) => selectFiles(Array.from(event.target.files || []))} /></label></div>
      <small>{files.length ? `${files.length} desteklenen dosya hazır` : "Klasörü sürükleyip doğrudan bu alana bırakabilirsiniz."}</small>
    </div>
    <label>Fatura yönü<select name="direction" defaultValue="auto"><option value="auto">Otomatik belirle</option><option value="in">Alış (UBL, e-Arşiv/SMM PDF veya yurt dışı hizmet Excel’i)</option><option value="out">Satış (yalnız UBL)</option></select></label>
    <button className="primary-button compact" disabled={busy || scanning || !taxpayerId || !files.length}>{busy ? `${progress.current}/${progress.total} grup · ${progress.processed}/${files.length} dosya` : "Dosyaları işle"}</button>
    {error && <div className="form-error full">{error}</div>}
    {result && <div className={`invoice-import-result ${result.status}`}>
      <span><strong>{result.invoice_count} fatura · {result.line_count} satır</strong><small>{result.batch_count} otomatik grup · {result.warning_count} uyarı · {result.error_count} hata</small></span>
      <em className={`pill ${result.status}`}>{result.status === "succeeded" ? "Tamamlandı" : result.status === "partial" ? "Kısmi" : "Başarısız"}</em>
      {result.issues.slice(0, 3).map((issue, index) => <small className="full" key={`${invoiceImportIssueText(issue)}-${index}`}>{invoiceImportIssueText(issue)}</small>)}
    </div>}
  </form>;
}

const documentTypeLabels: Record<Invoice["doc_type"], string> = {
  efatura: "e-Fatura",
  earsiv: "e-Arşiv",
  esmm: "e-SMM",
  emm: "e-MM",
  foreign_service: "Yurt dışı hizmet"
};


function directionLabel(directions: string[]): string {
  if (directions.includes("in") && directions.includes("out")) return "Gelen + Giden";
  if (directions.includes("in")) return "Gelen / Alış";
  if (directions.includes("out")) return "Giden / Satış";
  return "Yön belirtilmedi";
}

function InvoicePullForm({ taxpayers, onQueued }: { taxpayers: Taxpayer[]; onQueued: () => void | Promise<void> }) {
  const documentTypes = [{ value: "efatura", label: "e-Fatura" }, { value: "earsiv", label: "e-Arşiv" }];
  const connectedTaxpayers = taxpayers.filter((taxpayer) => taxpayer.integrator?.enabled && taxpayer.integrator.credentials_ready);
  const now = new Date(); const start = localDate(new Date(now.getFullYear(), now.getMonth(), 1)); const end = localDate(new Date(now.getFullYear(), now.getMonth() + 1, 0));
  const [form, setForm] = useState({ taxpayer: String(connectedTaxpayers[0]?.id || ""), start_date: start, end_date: end, direction: "both" }); const [busy, setBusy] = useState(false); const [message, setMessage] = useState("");
  const [docTypes, setDocTypes] = useState(documentTypes.map((item) => item.value));
  useEffect(() => {
    if (!connectedTaxpayers.some((taxpayer) => String(taxpayer.id) === form.taxpayer)) {
      const nextTaxpayer = String(connectedTaxpayers[0]?.id || "");
      if (nextTaxpayer !== form.taxpayer) {
        setForm((current) => ({ ...current, taxpayer: nextTaxpayer }));
      }
    }
  }, [connectedTaxpayers, form.taxpayer]);
  function field(name: string, value: string) { setForm((old) => ({ ...old, [name]: value })); }
  function toggleDocType(value: string) { setDocTypes((current) => current.includes(value) ? current.filter((item) => item !== value) : [...current, value]); }
  async function submit(e: FormEvent) { e.preventDefault(); setBusy(true); setMessage(""); try { const directions = form.direction === "both" ? ["in", "out"] : [form.direction]; for (const selectedType of docTypes) {
 const selectedDirections = selectedType === "earsiv" ? directions.filter(d => d === "out") : directions;
 if (selectedDirections.length) await api("/api/integrators/sync-jobs", { method: "POST", body: JSON.stringify({ taxpayer: Number(form.taxpayer), start_date: form.start_date, end_date: form.end_date, doc_types: [selectedType], directions: selectedDirections }) });
 } setMessage("Entegratör fatura çekme işlemi kuyruğa alındı; durum otomatik güncellenecek."); await onQueued(); } catch (r) { setMessage(r instanceof Error ? r.message : "İşlem başlatılamadı."); } finally { setBusy(false); } }
  return <form className="sync-form" onSubmit={submit}><label>Mükellef<select required value={form.taxpayer} onChange={(e) => field("taxpayer", e.target.value)}>{connectedTaxpayers.map((t) => <option value={t.id} key={t.id}>{t.vkn} · {t.title} · {t.integrator?.provider_label}</option>)}</select></label>{!connectedTaxpayers.length && <small className="credential-warning">Önce bir mükellefe aktif entegratör bağlantısı tanımlayın.</small>}<label>Başlangıç<input required type="date" value={form.start_date} onChange={(e) => field("start_date", e.target.value)} /></label><label>Bitiş<input required type="date" value={form.end_date} onChange={(e) => field("end_date", e.target.value)} /></label><label>Yön<select required value={form.direction} onChange={(e) => field("direction", e.target.value)}><option value="both">Her ikisi (Gelen + Giden)</option><option value="in">Gelen / Alış</option><option value="out">Giden / Satış</option></select></label><fieldset className="doc-type-options"><legend>Belge türleri (alış e-Arşivleri PDF ile yüklenir)</legend>{documentTypes.map((item) => <label className="doc-type-option" key={item.value}><input type="checkbox" checked={docTypes.includes(item.value) && !(item.value === "earsiv" && form.direction === "in")} disabled={item.value === "earsiv" && form.direction === "in"} onChange={() => toggleDocType(item.value)} />{item.label}</label>)}</fieldset><button className="primary-button" disabled={busy || !connectedTaxpayers.length || !docTypes.some(type => type !== "earsiv" || form.direction !== "in")}>{busy ? "Başlatılıyor…" : "Entegratörden çek"}</button>{message && <small>{message}</small>}</form>;
}

const kindLabels: Record<RefundEntry["kind"], string> = {
  input_vat: "İndirilecek KDV",
  loaded_vat: "Yüklenilen KDV",
  sales_invoice: "Satış faturaları",
  customs_declaration: "GÇB / ETGB",
  refund_calculation: "İade hesaplama",
  reduced_rate_sales: "İndirimli oran satışları"
};

const workspaceFamilyLabels: Record<RefundWorkspaceFamily, string> = {
  goods_export: "Mal ihracatı",
  reduced_rate: "İndirimli oran",
  withholding: "Tevkifat",
  service_exemption: "Hizmet tam istisnası",
  investment_project: "Yatırım / proje istisnası",
  other_special: "Diğer özel iade"
};

function RefundCaseWorkspace({ refundCase, onBack, canPrepare, canApprove, onReviewInvoices }: {
  refundCase: RefundCase;
  onBack: () => void;
  canPrepare: boolean;
  canApprove: boolean;
  onReviewInvoices: (request: InvoiceReviewRequest) => void;
}) {
  const legislationNotice = <LegislationCaseNotice refundCase={refundCase} />;
  if (refundCase.transaction_code === "301" || refundCase.workspace_family === "reduced_rate") {
    return <>{legislationNotice}<SmartRefundWorkspace key={refundCase.id} refundCase={refundCase} canPrepare={canPrepare} canApprove={canApprove} onBack={onBack} onReviewInvoices={onReviewInvoices} /></>;
  }
  return <>{legislationNotice}<RefundWorkspace refundCase={refundCase} workspaceFamily={refundCase.workspace_family} onBack={onBack} canPrepare={canPrepare} canApprove={canApprove} /></>;
}

function LegislationCaseNotice({ refundCase }: { refundCase: RefundCase }) {
  if (!refundCase.legislation_status.warnings.length) return null;
  return <div className={`legislation-case-warning ${refundCase.legislation_status.can_finalize ? "warning" : "blocked"}`}>
    <strong>{refundCase.legislation_status.can_finalize ? "Mevzuat incelemesi sürüyor" : "Nihai hesap mevzuat onayı bekliyor"}</strong>
    <span>{refundCase.legislation_status.warnings.map((item) => item.message).join(" ")}</span>
  </div>;
}

export function RefundWorkspace({ refundCase, workspaceFamily, onBack, canPrepare, canApprove }: { refundCase: RefundCase; workspaceFamily: RefundWorkspaceFamily; onBack: () => void; canPrepare: boolean; canApprove: boolean }) {
  const [entries, setEntries] = useState<RefundEntry[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [result, setResult] = useState<CalculationResult | null>(null);
  const [calculating, setCalculating] = useState(false);
  const [calculationError, setCalculationError] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState({ source_invoice: "", load_type: "1", eligible_vat: "", allocation_basis: "", cumulative_atik_confirmed: "false" });
  const [sales, setSales] = useState({ source_invoice: "", customs_registration_no: "", gtip: "", quantity: "", unit_code: "ADET", declaration_type: "GÇB", amount_try: "" });
  const [customs, setCustoms] = useState({ registration_no: "", currency: "USD", invoice_amount: "", declaration_type: "GÇB" });
  const [sourceKind, setSourceKind] = useState<DataImportBatch["kind"]>("eledger");
  const [sourceFile, setSourceFile] = useState<File | null>(null);
  const [priorInputFiles, setPriorInputFiles] = useState<File[]>([]);
  const [reconciliation, setReconciliation] = useState<SourceReconciliation | null>(null);
  const [documents, setDocuments] = useState<CaseDocument[]>([]);
  const [documentKind, setDocumentKind] = useState("other");
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [documentIdentity, setDocumentIdentity] = useState({
    document_number: "",
    document_date: "",
    issuing_authority: ""
  });
  const [submissionPackage, setSubmissionPackage] = useState<SubmissionPackage | null>(null);
  const [submissionCapability, setSubmissionCapability] = useState<SubmissionCapability | null>(null);
  const [semanticReadiness, setSemanticReadiness] = useState<SemanticReadiness | null>(null);
  const [portalListCode, setPortalListCode] = useState("");
  const [portalListFile, setPortalListFile] = useState<File | null>(null);

  async function loadWorkspace() {
    const query = new URLSearchParams({ taxpayer: String(refundCase.taxpayer), start_date: refundCase.period_start, end_date: refundCase.period_end });
    const [entryData, invoiceData, documentData, packages, capability, identity, readinessData] = await Promise.all([
      api<RefundEntry[]>(`/api/refund-cases/${refundCase.id}/entries`),
      api<Invoice[]>(`/api/invoices/?${query}`),
      api<CaseDocument[]>(`/api/refund-cases/${refundCase.id}/documents`),
      api<SubmissionPackage[]>(`/api/refund-cases/${refundCase.id}/submission-packages`),
      api<SubmissionCapability>("/api/submission-capability"),
      api<{ document_number: string; document_date: string; issuing_authority: string }>(`/api/refund-cases/${refundCase.id}/document-identity`),
      api<{ semantic: SemanticReadiness }>(`/api/refund-cases/${refundCase.id}/readiness`)
    ]);
    setEntries(entryData); setInvoices(invoiceData);
    setDocuments(documentData); setSubmissionPackage(packages[0] || null); setSubmissionCapability(capability);
    setDocumentIdentity(identity);
    setSemanticReadiness(readinessData.semantic);
    if (!portalListCode && readinessData.semantic.policy.official_outputs.length) {
      setPortalListCode(readinessData.semantic.policy.official_outputs[0]);
    }
    try {
      setReconciliation(await api<SourceReconciliation>(`/api/refund-cases/${refundCase.id}/source-reconciliation`));
    } catch {
      setReconciliation(null);
    }
    const calculation = entryData.find((item) => item.kind === "refund_calculation");
    if (calculation) setResult({ summary: calculation.payload, issues: [] });
  }

  useEffect(() => { loadWorkspace().catch((e) => setMessage(e.message)); }, [refundCase.id]);
  const incoming = invoices.filter((item) => item.direction === "in");
  const outgoing = invoices.filter((item) => item.direction === "out");
  const requiresCustoms = refundCase.transaction_code === "301" || refundCase.transaction_code === "338";
  const isWithholding = workspaceFamily === "withholding";
  const isServiceExemption = workspaceFamily === "service_exemption";
  const isInvestmentProject = workspaceFamily === "investment_project";

  async function run(action: () => Promise<unknown>, success: string) {
    setBusy(true); setMessage("");
    try { await action(); setMessage(success); await loadWorkspace(); return true; }
    catch (reason) { setMessage(reason instanceof Error ? reason.message : "İşlem tamamlanamadı."); return false; }
    finally { setBusy(false); }
  }

  async function addEntry(event: FormEvent, kind: RefundEntry["kind"], form: Record<string, string>, reset: () => void) {
    event.preventDefault();
    const sourceInvoice = form.source_invoice;
    const payload = Object.fromEntries(Object.entries(form).filter(([key]) => key !== "source_invoice"));
    const saved = await run(
      () => api(`/api/refund-cases/${refundCase.id}/entries`, { method: "POST", body: JSON.stringify({ kind, source_invoice: sourceInvoice ? Number(sourceInvoice) : null, payload }) }),
      "Satır eklendi."
    );
    if (saved) reset();
  }

  async function uploadCustoms(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const data = new FormData(formElement);
    const saved = await run(() => api(`/api/refund-cases/${refundCase.id}/import-customs`, { method: "POST", body: data }), "GÇB Excel'i içe aktarıldı.");
    if (saved) formElement.reset();
  }

  async function calculate() {
    setCalculating(true); setCalculationError("");
    setBusy(true); setMessage("");
    try {
      const data = await api<CalculationResult>(`/api/refund-cases/${refundCase.id}/calculate`, { method: "POST" });
      setResult(data); setMessage("Hesaplama ve kontroller tamamlandı."); await loadWorkspace(); setResult(data);
    } catch (reason) {
      const error = reason instanceof Error ? reason.message : "Hesaplanamadı.";
      setCalculationError(error); setMessage(error);
    }
    finally { setBusy(false); setCalculating(false); }
  }

  async function importSourceData() {
    if (!sourceFile) { setMessage("Aktarım dosyasını seçin."); return; }
    const payload = new FormData();
    payload.append("file", sourceFile);
    payload.append("kind", sourceKind);
    payload.append("refund_case", String(refundCase.id));
    const saved = await run(
      () => api(sourceKind === "kdv1" ? `/api/refund-cases/${refundCase.id}/monthly-vat/import` : `/api/taxpayers/${refundCase.taxpayer}/data-imports`, { method: "POST", body: payload }),
      "Kaynak veri güvenli biçimde aktarıldı ve mutabakata alındı."
    );
    if (saved) setSourceFile(null);
  }

  async function importPriorInput() {
    if (!priorInputFiles.length) return;
    const saved = await run(async () => {
      for (const file of priorInputFiles) {
        const payload = new FormData();
        payload.append("file", file);
        payload.append("kind", "prior_input_vat");
        payload.append("refund_case", String(refundCase.id));
        await api(`/api/taxpayers/${refundCase.taxpayer}/data-imports`, { method: "POST", body: payload });
      }
    }, "Önceki dönem indirilecek KDV listeleri aktarıldı.");
    if (saved) setPriorInputFiles([]);
  }

  async function importOfficialPortalList() {
    if (!portalListFile || !portalListCode) { setMessage("Resmî liste türünü ve kaynak dosyayı seçin."); return; }
    const payload = new FormData();
    payload.append("file", portalListFile);
    const saved = await run(
      () => api(`/api/refund-cases/${refundCase.id}/portal-lists/${portalListCode}/import`, { method: "POST", body: payload }),
      "Resmî liste ortak canonical modele aktarıldı."
    );
    if (saved) setPortalListFile(null);
  }

  async function reconcile() {
    await run(
      async () => { setReconciliation(await api<SourceReconciliation>(`/api/refund-cases/${refundCase.id}/source-reconciliation`, { method: "POST" })); },
      "Fatura, e-Defter/muavin, stok ve KDV1 mutabakatı yenilendi."
    );
  }

  async function uploadDocument() {
    if (!documentFile) { setMessage("Dayanak dosyasını seçin."); return; }
    const payload = new FormData();
    payload.append("file", documentFile);
    payload.append("kind", documentKind);
    Object.entries(documentIdentity).forEach(([key, value]) => payload.append(key, value.trim()));
    const saved = await run(
      () => api(`/api/refund-cases/${refundCase.id}/documents`, { method: "POST", body: payload }),
      "Dayanak belgesi hash'iyle değişmez kayda alındı."
    );
    if (saved) {
      setDocumentFile(null);
    }
  }

  async function saveDocumentIdentity() {
    await run(
      () => api(`/api/refund-cases/${refundCase.id}/document-identity`, { method: "PUT", body: JSON.stringify(documentIdentity) }),
      "Belge bilgileri kaydedildi."
    );
  }

  async function preparePackage() {
    await run(
      async () => { setSubmissionPackage(await api<SubmissionPackage>(`/api/refund-cases/${refundCase.id}/submission-packages`, { method: "POST" })); },
      "Yeni başvuru paketi sürümü hazırlandı."
    );
  }

  async function approvePackage() {
    if (!submissionPackage) return;
    await run(
      () => api(`/api/submission-packages/${submissionPackage.id}/approve`, { method: "POST", body: JSON.stringify({ note: "Muhasebe kontrolü tamamlandı." }) }),
      "Paket değişmez içerik hash'i üzerinden onaylandı."
    );
  }

  async function downloadPackage() {
    if (!submissionPackage) return;
    setBusy(true);
    try {
      const file = await apiFile(`/api/submission-packages/${submissionPackage.id}/artifact`);
      const url = URL.createObjectURL(file.blob);
      const link = document.createElement("a");
      link.href = url; link.download = file.filename || `${refundCase.taxpayer_vkn}-iade-paketi-v${submissionPackage.version}.zip`;
      document.body.appendChild(link); link.click(); link.remove(); URL.revokeObjectURL(url);
    } catch (reason) { setMessage(reason instanceof Error ? reason.message : "Paket indirilemedi."); }
    finally { setBusy(false); }
  }

  const calculatedAmount = calculationAmount(result);
  const calculationLabel = calculating ? "Hesaplanıyor…" : calculationError || (result && calculatedAmount === null) ? "Hesaplama yapılamadı" : calculatedAmount === null ? "Henüz hesaplanmadı" : formatMoney(calculatedAmount);
  return <div className={`refund-workspace ${canPrepare ? "" : "director-review-mode"}`}>
    <button className="text-button back-button review-action" onClick={onBack}>← İade dosyaları</button>
    <DebugTransferControls scope="case" targetId={refundCase.id} disabled={busy || calculating} />
    <section className="panel case-summary"><div><p className="eyebrow">{refundCase.transaction_name.toLocaleUpperCase("tr-TR")} · {refundCase.transaction_code}</p><h2>{refundCase.title}</h2><small>{refundCase.taxpayer_vkn} · {refundCase.taxpayer_title} · {refundCase.period_start} — {refundCase.period_end}</small></div><div><span>Hesaplanan iade</span><strong>{calculationLabel}</strong><small>Genel oran %{refundCase.general_vat_rate}</small></div></section>
    <div className="notice"><strong>{workspaceFamilyLabels[workspaceFamily]} çalışma alanı.</strong> Ortak belge, liste, mutabakat ve paket hazırlığı kullanılır; 301'e özgü stok, üretim ve kapasite akışı bu dosyaya uygulanmaz.</div>
    {semanticReadiness && <section className="panel semantic-readiness-panel">
      <div className="panel-head"><div><p className="eyebrow">SEMANTIC YETERLİLİK</p><h2>{semanticReadiness.status} · %{semanticReadiness.progress}</h2></div><em className={`pill ${semanticReadiness.ready ? "active" : "partial"}`}>{semanticReadiness.ready ? "Veri yeterli" : "Bilgi gerekli"}</em></div>
      {semanticReadiness.missing_semantics.length ? <div className="table-list">{semanticReadiness.missing_semantics.map((item) => <div className="table-row" key={item.key}><span><strong>{item.label}</strong><small>{item.reason}</small></span><span><small>Elde edilebilecek kaynaklar</small><strong>{item.possible_sources.join(" · ")}</strong></span></div>)}</div> : <p className="muted">İşlem policy'sinin zorunlu semantic gerçekleri elde edildi. Mutabakat ve hukuki kontroller ayrıca uygulanır.</p>}
      {semanticReadiness.mapping_gaps.length > 0 && <small>Mapping bekleyen alanlar: {semanticReadiness.mapping_gaps.flatMap((item) => item.fields).join(", ")}</small>}
    </section>}
    {canApprove && <div className="notice">Direktör inceleme modu: hazırlık verileri değiştirilemez; hazır başvuru paketi incelenip onaylanabilir.</div>}
    {message && <div className="notice">{message}</div>}
    {!invoices.length && <div className="notice warning">Bu dönem için fatura yok. Önce Faturalar ekranından entegratör bağlantısıyla çekin veya dosya yükleyin.</div>}

    <section className="workspace-grid">
      <article className="panel span-two"><div className="panel-head"><div><p className="eyebrow">1 · ORTAK KAYNAKLAR</p><h2>e-Defter, muavin ve KDV1</h2></div><em className={`pill ${reconciliation?.status === "matched" ? "active" : reconciliation?.status === "conflict" ? "failed" : "partial"}`}>{reconciliation?.status === "matched" ? "Mutabık" : reconciliation?.status === "conflict" ? "Fark var" : "Kontrol bekliyor"}</em></div><div className="source-import-row"><select value={sourceKind} onChange={(event) => setSourceKind(event.target.value as DataImportBatch["kind"])}><option value="eledger">e-Defter XML</option><option value="general_ledger">Muavin</option><option value="kdv1">KDV1</option></select><input type="file" accept=".pdf,.xml,.xlsx,.csv" onChange={(event) => setSourceFile(event.target.files?.[0] || null)} /><button className="secondary-button compact" disabled={busy || !sourceFile} onClick={() => void importSourceData()}>Güvenli aktar</button><button className="secondary-button compact" disabled={busy} onClick={() => void reconcile()}>Mutabakatı yenile</button></div>{reconciliation && <small>{Number(reconciliation.summary.matched_count || 0)} mutabık · {Number(reconciliation.summary.pending_count || 0)} bekleyen · {Number(reconciliation.summary.conflict_count || 0)} fark · kanıt {reconciliation.input_hash.slice(0, 12)}…</small>}</article>

      {semanticReadiness && <article className="panel span-two"><div className="panel-head"><div><p className="eyebrow">GİB RESMÎ LİSTELERİ</p><h2>Canonical liste kaynağı yükle</h2></div></div><div className="source-import-row"><select value={portalListCode} onChange={(event) => setPortalListCode(event.target.value)}>{semanticReadiness.policy.official_outputs.map((code) => <option value={code} key={code}>{code}</option>)}</select><input type="file" accept=".xls,.xlsx,.csv,.json,.xml" onChange={(event) => setPortalListFile(event.target.files?.[0] || null)} /><button className="secondary-button compact" disabled={busy || !portalListFile || !portalListCode} onClick={() => void importOfficialPortalList()}>Listeyi aktar</button></div><small>Bunlar zorunlu dosya uzantıları değil, şu anda desteklenen parser kaynaklarıdır. Aynı semantic veri fatura, ERP veya manuel eşlemeden de üretilebilir.</small></article>}

      <article className="panel"><div className="panel-head"><div><p className="eyebrow">1 · ALIŞLAR</p><h2>İndirilecek KDV</h2></div><span className="count">{entries.filter((e) => e.kind === "input_vat").length}</span></div><p className="muted">Dönemdeki entegratör veya dosya kaynaklı alış faturalarından hazırlanır.</p><button className="primary-button" disabled={busy || !incoming.length} onClick={() => run(() => api(`/api/refund-cases/${refundCase.id}/prepare-input`, { method: "POST" }), "İndirilecek KDV listesi hazırlandı.")}>Listeyi hazırla</button></article>

      <article className="panel"><div className="panel-head"><div><p className="eyebrow">2 · YÜKLENİM</p><h2>Yüklenilen KDV</h2></div><span className="count">{entries.filter((e) => e.kind === "loaded_vat").length}</span></div><form className="entry-form" onSubmit={(e) => addEntry(e, "loaded_vat", loaded, () => setLoaded({ source_invoice: "", load_type: "1", eligible_vat: "", allocation_basis: "", cumulative_atik_confirmed: "false" }))}><label>Alış faturası<select required value={loaded.source_invoice} onChange={(e) => setLoaded({ ...loaded, source_invoice: e.target.value })}><option value="">Seçin</option>{incoming.map((i) => <option value={i.id} key={i.id}>{i.issue_date} · {i.invoice_no} · KDV {i.tax_amount ?? "—"}</option>)}</select></label><div className="form-pair"><label>Tür<select value={loaded.load_type} onChange={(e) => setLoaded({ ...loaded, load_type: e.target.value })}><option value="1">1 · Doğrudan</option><option value="2">2 · Genel gider</option><option value="3">3 · ATİK</option></select></label><label>Bünyeye giren KDV<MoneyInput required min="0.01" value={loaded.eligible_vat} onChange={(value) => setLoaded({ ...loaded, eligible_vat: value })} /></label></div>{loaded.load_type === "2" && <label>Dağıtım anahtarı ve gerekçesi<input required value={loaded.allocation_basis} onChange={(e) => setLoaded({ ...loaded, allocation_basis: e.target.value })} /></label>}{loaded.load_type === "3" && <label className="checkbox-label"><input type="checkbox" checked={loaded.cumulative_atik_confirmed === "true"} onChange={(e) => setLoaded({ ...loaded, cumulative_atik_confirmed: e.target.checked ? "true" : "false" })} />Önceki dönem ATİK payları ve kalan KDV kontrol edildi</label>}<button className="primary-button" disabled={busy}>Yüklenime ekle</button></form></article>

      <article className="panel span-two"><h2>Önceki dönem indirilecek KDV listeleri (opsiyonel)</h2><p>Fatura tarihine göre dönem başlangıcından önceki 24 aya ait listeleri tek dosyada veya ayrı dosyalarda yükleyin.</p><div className="source-import-row"><input type="file" multiple accept=".xls,.xlsx,.csv" disabled={!canPrepare || busy} onChange={(event) => setPriorInputFiles(Array.from(event.target.files || []))} /><button className="secondary-button compact" disabled={!canPrepare || busy || !priorInputFiles.length} onClick={() => void importPriorInput()}>Listeleri yükle</button></div></article>
      {requiresCustoms && <article className="panel"><div className="panel-head"><div><p className="eyebrow">3 · GÜMRÜK</p><h2>GÇB / ETGB</h2></div><span className="count">{entries.filter((e) => e.kind === "customs_declaration").length}</span></div><form className="entry-form" onSubmit={(e) => addEntry(e, "customs_declaration", customs, () => setCustoms({ registration_no: "", currency: "USD", invoice_amount: "", declaration_type: "GÇB" }))}><label>Tescil numarası<input required value={customs.registration_no} onChange={(e) => setCustoms({ ...customs, registration_no: e.target.value })} /></label><div className="form-pair"><label>Para birimi<input required maxLength={3} value={customs.currency} onChange={(e) => setCustoms({ ...customs, currency: e.target.value.toUpperCase() })} /></label><label>Fatura tutarı<MoneyInput required value={customs.invoice_amount} onChange={(value) => setCustoms({ ...customs, invoice_amount: value })} /></label></div><label>Tür<select value={customs.declaration_type} onChange={(e) => setCustoms({ ...customs, declaration_type: e.target.value })}><option>GÇB</option><option>ETGB</option></select></label><button className="primary-button" disabled={busy}>GÇB ekle</button></form><form className="upload-form" onSubmit={uploadCustoms}><input required name="file" type="file" accept=".xlsx" /><button className="secondary-button" disabled={busy}>Excel'den yükle</button><small>Sütunlar: tescil no, döviz, tutar, tür</small></form></article>}

      <article className="panel span-two"><div className="panel-head"><div><p className="eyebrow">{requiresCustoms ? "4 · İHRACAT" : "3 · İŞLEM BELGELERİ"}</p><h2>{requiresCustoms ? "Satış faturası / GTİP satırı" : "İade hakkı doğuran satış faturası"}</h2></div><span className="count">{entries.filter((e) => e.kind === "sales_invoice").length}</span></div>{isServiceExemption ? <div className="notice"><strong>Hizmet faturaları kaynak kayıtlardan alınır.</strong> Aşağıdaki dayanak bölümünden fatura numarası bazında hizmet istisnası kanıt dökümünü yükleyin; işlem bedeli faturadan ve gerekiyorsa TCMB kurundan hesaplanır.</div> : <form className="entry-form sales-form" onSubmit={(e) => addEntry(e, "sales_invoice", sales, () => setSales({ source_invoice: "", customs_registration_no: "", gtip: "", quantity: "", unit_code: "ADET", declaration_type: "GÇB", amount_try: "" }))}><label>Satış faturası<select required value={sales.source_invoice} onChange={(e) => setSales({ ...sales, source_invoice: e.target.value })}><option value="">Seçin</option>{outgoing.map((i) => <option value={i.id} key={i.id}>{i.issue_date} · {i.invoice_no} · {i.receiver_name}</option>)}</select></label>{requiresCustoms && <><label>GÇB / ETGB tescil no<input required value={sales.customs_registration_no} onChange={(e) => setSales({ ...sales, customs_registration_no: e.target.value })} /></label><label>GTİP (12 rakam)<input required inputMode="numeric" minLength={12} maxLength={12} value={sales.gtip} onChange={(e) => setSales({ ...sales, gtip: e.target.value.replace(/\D/g, "") })} /></label></>}<div className="form-pair"><label>Miktar<input required type="number" step="0.001" value={sales.quantity} onChange={(e) => setSales({ ...sales, quantity: e.target.value })} /></label><label>Birim<input required value={sales.unit_code} onChange={(e) => setSales({ ...sales, unit_code: e.target.value })} /></label></div>{requiresCustoms && <label>Tür<select value={sales.declaration_type} onChange={(e) => setSales({ ...sales, declaration_type: e.target.value })}><option>GÇB</option><option>ETGB</option></select></label>}<label>İşlem bedeli (TL)<MoneyInput required min="0.01" value={sales.amount_try} onChange={(value) => setSales({ ...sales, amount_try: value })} /></label><button className="primary-button" disabled={busy}>Satış satırı ekle</button></form>}</article>

      <article className="panel"><div className="panel-head"><div><p className="eyebrow">5 · SONUÇ</p><h2>İade hesaplama</h2></div></div>{result && !calculating && !calculationError && calculatedAmount !== null ? <div className="calculation"><span>İşlem bedeli<strong>{moneyNumber(result.summary.delivery_amount)} TL</strong></span><span>Azami KDV<strong>{moneyNumber(result.summary.maximum_refundable_vat)} TL</strong></span><span>İade tutarı<strong>{moneyNumber(calculatedAmount)} TL</strong></span></div> : <p role="status">{calculationLabel}</p>}<button className="primary-button" disabled={busy} onClick={calculate}>Hesapla ve kontrol et</button>{result?.issues.map((issue) => <div className={`issue ${issue.severity}`} key={issue.code}>{formatMoneyText(issue.message)}</div>)}</article>
    </section>

    <section className="panel">
      <div className="panel-head"><div><p className="eyebrow">BELGE KİMLİĞİ</p><h2>Yüklenecek resmî belgenin bilgileri</h2></div></div>
      <div className="legislation-meta">
        <label>Belge numarası<input required value={documentIdentity.document_number} onChange={(event) => setDocumentIdentity({ ...documentIdentity, document_number: event.target.value })} /></label>
        <label>Belge tarihi<input required type="date" value={documentIdentity.document_date} onChange={(event) => setDocumentIdentity({ ...documentIdentity, document_date: event.target.value })} /></label>
        <label>Düzenleyen kurum<input required value={documentIdentity.issuing_authority} onChange={(event) => setDocumentIdentity({ ...documentIdentity, issuing_authority: event.target.value })} /></label>
        <button className="primary-button compact document-identity-save" disabled={busy || !Object.values(documentIdentity).every((value) => value.trim())} onClick={() => void saveDocumentIdentity()}>Kaydet</button>
      </div>
      <small>Bu bilgiler iade dosyasına kaydedilir ve resmî dayanak yüklenirken kullanılır.</small>
    </section>

    <section className="panel finalization-panel">
      <div className="panel-head"><div><p className="eyebrow">4 · KONTROL VE BAŞVURU</p><h2>Dayanaklar, çalışma kâğıdı ve sürümlü paket</h2></div>{submissionPackage && <em className={`pill ${submissionPackage.status === "ready" ? "active" : "partial"}`}>v{submissionPackage.version} · {submissionPackage.status}</em>}</div>
      <div className="finalization-grid">
        <div><strong>Dayanak belgeleri</strong><div className="source-import-row"><select value={documentKind} onChange={(event) => setDocumentKind(event.target.value)}>{isWithholding && <><option value="kdv2">KDV2 dökümü</option><option value="kdv2_payment">KDV2 ödeme dökümü</option><option value="general_budget_approval">Genel bütçe hakediş onayı</option><option value="tax_inspection_report">Vergi inceleme raporu</option><option value="full_certification">Tam tasdik sözleşmesi</option></>}{isServiceExemption && <><option value="service_evidence">Hizmet istisnası kanıt dökümü</option>{refundCase.transaction_code === "302" && <option value="service_payment">Hizmet bedeli ödeme dökümü</option>}{refundCase.transaction_code === "303" && <option value="roaming_reciprocity">Roaming karşılıklılık belgesi</option>}{refundCase.transaction_code === "305" && <option value="port_airport_evidence">Liman/hava meydanı hizmet kanıtı</option>}{refundCase.transaction_code === "311" && <><option value="transport_authority">Taşıma yetki ve taşıt belgesi</option><option value="transport_document">Uluslararası taşıma belgesi</option></>}{refundCase.transaction_code === "316" && <option value="free_zone_movement">Serbest bölge giriş/çıkış belgesi</option>}{refundCase.transaction_code === "334" && <><option value="health_license">Sağlık Bakanlığı izin belgesi</option><option value="patient_identity">Yabancı hasta pasaport/mavi kart belgesi</option></>}</>}{isInvestmentProject && <><option value="project_scope">Belge/proje kapsam ve kullanım dökümü</option><option value="project_authorization">Proje/yetki dayanak belgesi</option><option value="investment_notification">Yatırım teşvik istisna bildirimi</option><option value="donation_protocol">Bağış protokolü</option><option value="building_permit">Yapı ruhsatı</option><option value="project_progress">Proje ilerleme/uygunluk bildirimi</option><option value="industrial_registry">Sanayi sicil belgesi</option><option value="minimum_investment_proof">Asgari yatırım tutarı kanıtı</option></>}<option value="special_refund_evidence">Özel iade kapsam/fatura dökümü</option><option value="approved_scope_list">Onaylı kapsam listesi</option><option value="special_invoice">Gümrük onaylı özel fatura</option><option value="payment_proof">Ödeme/KDV iade belgesi</option><option value="title_deed">Tapu/ilk teslim belgesi</option><option value="product_registration">Ürün tescil/izin belgesi</option><option value="exchange_certificate">Borsa/ürün senedi teyidi</option><option value="exemption_certificate">İstisna belgesi</option><option value="investment_certificate">Yatırım teşvik belgesi</option><option value="capacity_report">Kapasite raporu</option><option value="ymm_report">YMM raporu</option><option value="guarantee">Teminat</option><option value="kdv1">KDV1</option><option value="other">Diğer</option></select><input type="file" accept={documentKind === "capacity_report" ? ".xlsx" : ".pdf,.xml,.xlsx,.xls,.csv,.zip"} onChange={(event) => setDocumentFile(event.target.files?.[0] || null)} /><button className="secondary-button compact" disabled={busy || !documentFile} onClick={() => void uploadDocument()}>Yükle</button></div><small>{documents.length ? documents.map((item) => `${item.kind}: ${item.filename}`).join(" · ") : "Henüz dayanak yüklenmedi."}</small>{isWithholding && <small>KDV2, ödeme ve hakediş dökümlerini fatura numarası bazında XLSX/CSV yükleyin.</small>}{isServiceExemption && <small>Hizmet kanıt ve 302 ödeme dökümlerini fatura numarası bazında XLSX/CSV yükleyin.</small>}{isInvestmentProject && <small>Onaylı belge/proje satırını fatura satırı ve kullanılan limit ile birlikte XLSX/CSV olarak yükleyin.</small>}</div>
        <div><strong>Başvuru çıktıları</strong><div className="refund-head-actions"><a className="secondary-button compact" href={`/api/refund-cases/${refundCase.id}/working-paper`}>Çalışma kâğıdı</a><button className="secondary-button compact" disabled={busy} onClick={() => void preparePackage()}>Yeni paket sürümü</button>{submissionPackage?.status === "ready" && <button className="secondary-button compact review-action" disabled={busy} onClick={() => void downloadPackage()}>Paketi indir</button>}{canApprove && submissionPackage?.status === "ready" && !submissionPackage.approval && <button className="primary-button compact review-action" disabled={busy} onClick={() => void approvePackage()}>Direktör onayı</button>}</div>{submissionPackage?.readiness?.blockers?.length ? <small>{submissionPackage.readiness.blockers.map((item) => typeof item === "string" ? item : item.message || item.detail || item.code).join(" · ")}</small> : <small>Readiness kontrolleri paket sürümünde sabitlenir.</small>}</div>
      </div>
      {submissionCapability && <div className="notice warning"><strong>Gerçek GİB gönderimi kapalı.</strong> {submissionCapability.reason} Paket manuel yüklemeye hazırdır; testte dış sisteme veri gönderilmez.</div>}
    </section>

    <section className="panel"><div className="panel-head"><div><p className="eyebrow">KAYITLAR</p><h2>Hazırlanan listeler</h2></div></div>{entries.length ? <div className="entry-groups">{(Object.keys(kindLabels) as RefundEntry["kind"][]).map((kind) => { const rows = entries.filter((e) => e.kind === kind); return <div className="entry-group" key={kind}><div><strong>{kindLabels[kind]}</strong><a href={`/api/refund-cases/${refundCase.id}/export/${kind}`}>Excel indir</a></div>{rows.length ? rows.map((entry) => <div className="entry-row" key={entry.id}><span>{entry.source_invoice_no || entry.payload.registration_no || entry.payload.period || `Satır ${entry.row_number}`}</span><small>{entry.payload.eligible_vat ? `${moneyNumber(entry.payload.eligible_vat)} TL` : entry.payload.gtip || (entry.payload.total_loaded_vat ? formatMoney(entry.payload.total_loaded_vat) : "")}</small>{kind !== "refund_calculation" && <button disabled={busy} onClick={() => run(() => api(`/api/refund-entries/${entry.id}`, { method: "DELETE" }), "Satır silindi.")}>Sil</button>}</div>) : <small>Henüz hazırlanmadı.</small>}</div>; })}</div> : <Empty>Henüz liste satırı yok.</Empty>}</section>
  </div>;
}

function RefundCaseForm({ taxpayers, taxRules, onSaved }: { taxpayers: Taxpayer[]; taxRules: TaxRuleSnapshot; onSaved: () => void }) {
  const now = new Date();
  const thresholdYears = Object.keys(taxRules.reduced_rate_thresholds).map(Number).sort((a, b) => b - a);
  const annualYear = thresholdYears.find((year) => year < now.getFullYear()) || thresholdYears[0] || now.getFullYear() - 1;
  const first = localDate(new Date(now.getFullYear(), now.getMonth(), 1));
  const last = localDate(new Date(now.getFullYear(), now.getMonth() + 1, 0));
  const [form, setForm] = useState({
    taxpayer: String(taxpayers[0]?.id || ""),
    title: `${annualYear} indirimli oran iadesi`,
    period_start: first,
    period_end: last,
    claim_type: "reduced_rate_annual",
    refund_method: "offset",
    general_vat_rate: taxRules.general_vat_rate,
    transaction_code: "439",
    declared_transaction_base: "",
    declared_vat_free_input_base: "0",
    declared_refundable_vat: "",
    year: String(annualYear),
    start_month: "01",
    end_month: "12",
    non_refundable_threshold: taxRules.reduced_rate_thresholds[String(annualYear)] || "",
    claim_period: `${annualYear + 1}-01`
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  function field(name: string, value: string) {
    setForm((current) => ({ ...current, [name]: value }));
  }

  function changeTransaction(value: string) {
    const selected = taxRules.transaction_rules.find((item) => item.code === value);
    if (!selected) return;
    setForm((current) => ({
      ...current,
      transaction_code: value,
      claim_type: selected.claim_type,
      period_start: ["301", "338"].includes(value) ? "" : current.period_start,
      period_end: ["301", "338"].includes(value) ? "" : current.period_end,
      title: selected.claim_type === "reduced_rate_annual"
        ? `${current.year} indirimli oran iadesi`
        : selected.name
    }));
  }

  function changeMonthlyPeriod(value: string) {
    const [year, month] = value.split("-").map(Number);
    if (!year || !month) return;
    const period = new Date(year, month - 1, 1);
    const selected = taxRules.transaction_rules.find((item) => item.code === form.transaction_code);
    setForm((current) => ({
      ...current,
      period_start: localDate(period),
      period_end: localDate(new Date(year, month, 0)),
      title: selected
        ? `${period.toLocaleString("tr-TR", { month: "long", year: "numeric" })} ${selected.name.toLocaleLowerCase("tr-TR")}`
        : current.title
    }));
  }

  function changeYear(value: string) {
    const numericYear = Number(value);
    setForm((current) => ({
      ...current,
      year: value,
      title: `${value} indirimli oran iadesi`,
      non_refundable_threshold: taxRules.reduced_rate_thresholds[value] || "",
      claim_period: Number.isFinite(numericYear) ? `${numericYear + 1}-01` : current.claim_period
    }));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const payload = form.claim_type === "reduced_rate_annual"
      ? {
          taxpayer: Number(form.taxpayer),
          title: form.title,
          period_start: `${form.year}-${form.start_month}-01`,
          period_end: localDate(new Date(Number(form.year), Number(form.end_month), 0)),
          claim_type: form.claim_type,
          transaction_code: "439",
          refund_method: form.refund_method,
          non_refundable_threshold: form.non_refundable_threshold || null,
          claim_period: `${form.claim_period}-01`
        }
      : {
          taxpayer: Number(form.taxpayer),
          title: form.title,
          period_start: form.period_start,
          period_end: form.period_end,
          claim_type: form.claim_type,
          refund_method: form.refund_method,
          general_vat_rate: form.general_vat_rate,
          transaction_code: form.transaction_code,
          declared_transaction_base: form.declared_transaction_base || "0",
          declared_vat_free_input_base: form.declared_vat_free_input_base || "0",
          declared_refundable_vat: form.declared_refundable_vat || "0"
        };
    try {
      await api("/api/refund-cases", { method: "POST", body: JSON.stringify(payload) });
      onSaved();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Kaydedilemedi.");
    } finally {
      setBusy(false);
    }
  }

  const years = thresholdYears;
  const selectedRule = taxRules.transaction_rules.find((item) => item.code === form.transaction_code);

  return <form className="case-form refund-case-form" onSubmit={submit}>
    <label>Mükellef<select required value={form.taxpayer} onChange={(event) => field("taxpayer", event.target.value)}>{taxpayers.map((taxpayer) => <option value={taxpayer.id} key={taxpayer.id}>{taxpayer.vkn} · {taxpayer.title}</option>)}</select></label>
    <label>İade işlemi<select value={form.transaction_code} onChange={(event) => changeTransaction(event.target.value)}>{taxRules.transaction_rules.map((item) => <option value={item.code} key={item.code}>{item.code} · {item.name}</option>)}</select><small>{selectedRule?.source_reference}</small></label>
    <label className="case-title-field">Dosya adı<input required value={form.title} onChange={(event) => field("title", event.target.value)} /></label>
    {form.claim_type === "reduced_rate_annual" ? <>
      <label>Hak kazanılan yıl<select value={form.year} onChange={(event) => changeYear(event.target.value)}>{years.map((year) => <option value={year} key={year}>{year}</option>)}</select></label>
      <label>İşlem başlangıç ayı<select value={form.start_month} onChange={(event) => setForm(current => ({ ...current, start_month: event.target.value, end_month: current.end_month < event.target.value ? event.target.value : current.end_month }))}>{Array.from({ length: 12 }, (_, index) => { const month = String(index + 1).padStart(2, "0"); return <option key={month} value={month}>{new Date(2000, index, 1).toLocaleString("tr-TR", { month: "long" })}</option>; })}</select></label>
      <label>İşlem bitiş ayı<select value={form.end_month} onChange={(event) => field("end_month", event.target.value)}>{Array.from({ length: 12 }, (_, index) => { const month = String(index + 1).padStart(2, "0"); return <option key={month} value={month} disabled={month < form.start_month}>{new Date(2000, index, 1).toLocaleString("tr-TR", { month: "long" })}</option>; })}</select><small>Tek ay veya aynı yıl içinde birden fazla ay seçebilirsiniz. Yıllık alt sınır uygulanır.</small></label>
      <label>İade talep dönemi<input required type="month" min={`${Number(form.year) + 1}-01`} max={`${Number(form.year) + 1}-11`} value={form.claim_period} onChange={(event) => field("claim_period", event.target.value)} /></label>
      <label>Yasal alt sınır (TL)<input readOnly value={form.non_refundable_threshold ? moneyNumber(form.non_refundable_threshold) : ""} /><small>Yürürlük tarihli GİB kuralından gelir.</small></label>
    </> : <>
      {["301", "338"].includes(form.transaction_code)
        ? <label>İade talep dönemi<input required type="month" value={form.period_start.slice(0, 7)} onChange={(event) => changeMonthlyPeriod(event.target.value)} /><small>GÇB kapanış tarihinin ve KDV1 beyannamesinin ait olduğu ay.</small></label>
        : <><label>Başlangıç<input required type="date" value={form.period_start} onChange={(event) => field("period_start", event.target.value)} /></label><label>Bitiş<input required type="date" value={form.period_end} onChange={(event) => field("period_end", event.target.value)} /></label></>}
      <label>Genel KDV oranı (%)<input required type="number" min="0" max="100" step="0.01" value={form.general_vat_rate} onChange={(event) => field("general_vat_rate", event.target.value)} /></label>
      <label>İade hakkı doğuran işlem bedeli<MoneyInput required min="0" value={form.declared_transaction_base} onChange={(value) => field("declared_transaction_base", value)} /></label>
      {form.transaction_code === "338" && <label>KDV'siz temin edilen girdi bedeli<MoneyInput required min="0" value={form.declared_vat_free_input_base} onChange={(value) => field("declared_vat_free_input_base", value)} /></label>}
      {(form.claim_type === "withholding" || form.claim_type === "other_refund") && <label>Beyan edilen iade KDV'si<MoneyInput required min="0" value={form.declared_refundable_vat} onChange={(value) => field("declared_refundable_vat", value)} /></label>}
    </>}
    <label>Talep şekli<select value={form.refund_method} onChange={(event) => field("refund_method", event.target.value)}><option value="offset">Mahsuben</option><option value="cash">Nakden</option><option value="mixed">Karma</option></select></label>
    <button className="primary-button compact" disabled={busy}>{busy ? "Oluşturuluyor…" : "Dosyayı oluştur"}</button>
    {error && <div className="form-error full">{error}</div>}
  </form>;
}

export default App;
